/* eslint-disable react-hooks/exhaustive-deps */
import { useDispatch, useSelector } from "react-redux";
import { resourceUpdated, selectAllResources, selectResourceById } from "../slices/resourceCollection/resourceCollectionSlice";
import { memo, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import { Button, Modal, Segment } from "semantic-ui-react";
import CompositionPdfViewer from "./CompositionPdfViewer";
import scrollTo from "../functions/scrollTo";
import getIdFromReference from "../functions/getIdFromReference";
import Spinner from "./Spinner";
import { editModeOff, editModeOn, selectEditMode } from "../slices/editMode/editModeSlice";
import { TextField, ToggleButton, ToggleButtonGroup } from "@mui/material";
import { selectAllResourceChanges } from "../slices/resourceChangeReport/resourceChangeReportSlice";
import updateChangeReport from "../slices/resourceChangeReport/updateChangeReport";
import ArrayEntry from "../datatypeComponents/ArrayEntry";
import NarrativeEntry, { emptyTextNoData } from "../datatypeComponents/NarrativeEntry";
import { changesCleared, selectChangedResourcesList, selectChangedResourcesListStatus } from "../slices/changedResourcesList/changedResourcesListSlice";
import updateFhirJson from "../functions/updateFhirJsonAPI";
import { selectCurrentUserApiToken, selectCurrentUserName } from "../slices/currentUser/currentUserSlice";
import { displayStringFromFHIRIdentifier } from "../datatypeComponents/IdentifierEntry";
import ProfilesByResourceType from "../dictionaries/ProfilesByResourceType";
import { compositionTypeCodeBaselineMeasureReport, compositionTypeCodeOutcomeMeasureReport, compositionTypeCodeParticipantFlowReport, compositionTypeCodeSummaryOfFindings, emptyReasonCodeableConceptSet, emptyReasonNotStarted, languageCodeDisplay, languageCodes, sectionCodeAssertion, sectionCodeCertaintyOfEvidence, sectionCodeColumnHeaders, sectionCodeComparatorGroup, sectionCodeGroupAssignment, sectionCodeGroups, sectionCodeInterventionGroup, sectionCodeOutcomeMeasure, sectionCodePopulation, sectionCodeResults, sectionCodeResultWithComparatorAlone, sectionCodeResultWithInterventionAlone, sectionCodeResultWithInterventionAloneCalculated, sectionCodeResultWithInterventionVsComparator, sectionCodeSampleSize, sectionCodeSummaryOfFindings, sectionCodeVariables } from "../dictionaries/CodeSystemLookup";
import PeriodEntry, { DateEntry, DateTimeEntry, displayStringFromFHIRPeriod, getDateDisplay, getDateTimeDisplay } from "../datatypeComponents/PeriodEntry";
import ReferenceEntry, { DisplayFromFHIRReference, displayStringFromFHIRReference, submitNewCompositionAndBundle } from "../datatypeComponents/ReferenceEntry";
import MarkdownEntry, { DisplayFromFHIRMarkdown } from "../datatypeComponents/MarkdownEntry";
import { DisplayFromFHIRUsageContext } from "../datatypeComponents/UsageContextEntry";
import { displayStringFromFHIRAnnotation } from "../datatypeComponents/AnnotationEntry";
import { DisplayFromFHIRContactDetail } from "../datatypeComponents/ContactDetailEntry";
import { DisplayFromFHIRAttester } from "../datatypeComponents/AttesterEntry";
import { DisplayFromFHIRRelatesTo } from "../datatypeComponents/RelatesToEntry";
import CodeEntry from "../datatypeComponents/CodeEntry";
import StringEntry from "../datatypeComponents/StringEntry";
import CodeableConceptEntry, { displayStringFromFHIRCodeableConcept } from "../datatypeComponents/CodeableConceptEntry";
import generateSummaryOfFindingsFromOutcomeMeasureReport from "../functions/generateSoFFromOMR";
import generateSummaryOfFindingsNarrative from "../functions/generateSoFNarrative";
import EvidenceTableDisplay, { generatedNarrativeFromEvidenceTable } from "./EvidenceTableDisplay";
import EvidenceTableEdit from "./EvidenceTableEdit";
import submitFhirJson from "../functions/submitFhirJsonAPI";
import { displayStringFromFHIRCoding } from "../datatypeComponents/CodingEntry";
import { displayStringFromFHIRExpression } from "../datatypeComponents/ExpressionEntry";
import { displayStringFromFHIRQuantity } from "../datatypeComponents/QuantityEntry";
import { displayStringFromFHIRRange } from "../datatypeComponents/RangeEntry";
import { displayStringFromFHIRRelativeTime } from "../datatypeComponents/RelativeTimeEntry";
import { DisplayFromFHIRExtendedContactDetail } from "../datatypeComponents/ExtendedContactDetailEntry";

const generateResourceTextNarrative = (resource) => {
    if (resource.text?.div && resource.text.status !== "empty") {
        return resource.text;
    }
    return null;
}

const addElementValuesForOutcomeMeasureReportFromComparativeEvidenceSynthesisReport = (resourceJson) => {
    if (!resourceJson) {
        return null;
    }
    let addElementValues = {
        "type": {
            "coding": [{
                "system": "https://fevir.net/resources/CodeSystem/179423",
                "code": "OutcomeMeasureReport", "display": "OutcomeMeasureReport"
            }],
            "text": "Outcome Measure Report"
        },
        "relatesTo": [
            {
                "type": "part-of",
                "targetReference": {
                    "reference": "Composition/" + resourceJson.id,
                    "type": "Composition",
                    "display": resourceJson.title
                }
            }
        ],
        "section": []
    };
    let populationReference;
    let interventionDescriptionReference;
    let comparatorDescriptionReference;
    let outcomeMeasuresReferences = [];
    for (const section of resourceJson.section) {
        let sectionCode = section?.code?.coding?.[0]?.code || section?.code?.text;
        if (sectionCode === "population" && section.entry?.[0]) {
            populationReference = section.entry[0];
        }
        if (sectionCode === "intervention-description" && section.entry?.[0]) {
            interventionDescriptionReference = section.entry[0];
        }
        if (sectionCode === "comparator-description" && section.entry?.[0]) {
            comparatorDescriptionReference = section.entry[0];
        }
        if (sectionCode === "variables" && section.entry?.length > 0) {
            outcomeMeasuresReferences = section.entry;
        }
    }
    let groupAssignmentReference;
    if (interventionDescriptionReference && comparatorDescriptionReference) {
        addElementValues.contained = [{
            "resourceType": "EvidenceVariable",
            "id": "group-assignment",
            "meta": { "profile": ["http://hl7.org/fhir/uv/ebm/StructureDefinition/group-assignment"] },
            "title": "GroupAssignment: " + interventionDescriptionReference.display + " vs. " + comparatorDescriptionReference.display,
            "status": "active",
            "definition": {
                "concept": {
                    "coding": [{
                        "system": "https://fevir.net/resources/CodeSystem/179423",
                        "code": "defined-in-handling-and-category",
                        "display": "Defined in handling and category elements"
                    }]
                }
            },
            "handling": "dichotomous",
            "category": [
                {
                    "name": interventionDescriptionReference.display,
                    "valueReference": interventionDescriptionReference
                },
                {
                    "name": comparatorDescriptionReference.display,
                    "valueReference": comparatorDescriptionReference
                }
            ]
        }];
        groupAssignmentReference = {
            "reference": "#group-assignment",
            "type": "EvidenceVariable",
            "display": "GroupAssignment: " + interventionDescriptionReference.display + " vs. " + comparatorDescriptionReference.display
        }
    }
    let groupListSection = {
        "title": "Outcome Group List",
        "code": { "text": "OutcomeGroupList" },
        "text": emptyTextNoData,
        "entry": [],
        "emptyReason": emptyReasonNotStarted
    }
    if (populationReference) {
        groupListSection.entry.push(populationReference);
    }
    if (interventionDescriptionReference) {
        groupListSection.entry.push(interventionDescriptionReference);
    }
    if (comparatorDescriptionReference) {
        groupListSection.entry.push(comparatorDescriptionReference);
    }
    if (groupAssignmentReference) {
        groupListSection.entry.push(groupAssignmentReference);
    }
    if (groupListSection.entry.length > 0) {
        delete groupListSection.emptyReason;
    }
    addElementValues.section.push(groupListSection);
    if (outcomeMeasuresReferences.length > 0) {
        for (const outcomeMeasure of outcomeMeasuresReferences) {
            if (outcomeMeasure.type === "EvidenceVariable") {
                let resultsEntry = {
                    "title": outcomeMeasure.display,
                    "code": {
                        "coding": [{
                            "system": "https://fevir.net/resources/CodeSystem/179423",
                            "code": "results", "display": "Results"
                        }]
                    },
                    "focus": outcomeMeasure,
                    "text": emptyTextNoData,
                    "entry": [],
                    "emptyReason": emptyReasonNotStarted
                };
                addElementValues.section.push(resultsEntry);
            }
        }
    }
    return addElementValues;
}

const addElementValuesForSummaryOfFindingsFromComparativeEvidenceSynthesisReport = (resourceJson) => {
    if (!resourceJson) {
        return null;
    }
    let addElementValues = {
        "type": {
            "coding": [{
                "system": "https://fevir.net/resources/CodeSystem/179423",
                "code": "SummaryOfFindings", "display": "SummaryOfFindings"
            }],
            "text": "Summar of Findings"
        },
        "relatesTo": [
            {
                "type": "part-of",
                "targetReference": {
                    "reference": "Composition/" + resourceJson.id,
                    "type": "Composition",
                    "display": resourceJson.title
                }
            }
        ],
        "section": [
            {
                "title": "Column Headers",
                "code": sectionCodeColumnHeaders,
                "section": [
                    {
                        "title": "Outcome Measure",
                        "code": sectionCodeOutcomeMeasure,
                        "text": {
                            "status": "generated",
                            "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\"><p>Outcome</p></div>"
                        }
                    },
                    {
                        "title": "Sample Size",
                        "code": sectionCodeSampleSize,
                        "text": {
                            "status": "generated",
                            "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\"><p>Sample Size</p></div>"
                        }
                    },
                    {
                        "title": "Result Without Treatment",
                        "code": sectionCodeResultWithComparatorAlone,
                        "text": {
                            "status": "generated",
                            "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\"><p>Result Without Treatment</p></div>"
                        }
                    },
                    {
                        "title": "Result With Treatment--Observed",
                        "code": sectionCodeResultWithInterventionAlone,
                        "text": {
                            "status": "generated",
                            "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\"><p>Result With Treatment (Observed)</p></div>"
                        }
                    },
                    {
                        "title": "Result With Treatment--Derived",
                        "code": sectionCodeResultWithInterventionAloneCalculated,
                        "text": {
                            "status": "generated",
                            "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\"><p>Result With Treatment (Calculated)</p></div>"
                        }
                    },
                    {
                        "title": "Effect Estimate",
                        "code": sectionCodeResultWithInterventionVsComparator,
                        "text": {
                            "status": "generated",
                            "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\"><p>Effect Estimate</p></div>"
                        }
                    },
                    {
                        "title": "Certainty of Evidence",
                        "code": sectionCodeCertaintyOfEvidence,
                        "text": {
                            "status": "generated",
                            "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\"><p>Certainty of Evidence</p></div>"
                        }
                    },
                    {
                        "title": "Assertion",
                        "code": sectionCodeAssertion,
                        "text": {
                            "status": "generated",
                            "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\"><p>What this means</p></div>"
                        }
                    }
                ]
            },
            {
                "title": "Summary of Findings",
                "code": sectionCodeSummaryOfFindings,
                "text": emptyTextNoData,
                "section": [],
                "emptyReason": emptyReasonNotStarted
            }
        ]
    };
    let populationReference;
    let interventionDescriptionReference;
    let comparatorDescriptionReference;
    let outcomeMeasuresReferences = [];
    for (const section of resourceJson.section) {
        let sectionCode = section?.code?.coding?.[0]?.code || section?.code?.text;
        if (sectionCode === "population" && section.entry?.[0]) {
            populationReference = section.entry[0];
        }
        if (sectionCode === "intervention-description" && section.entry?.[0]) {
            interventionDescriptionReference = section.entry[0];
        }
        if (sectionCode === "comparator-description" && section.entry?.[0]) {
            comparatorDescriptionReference = section.entry[0];
        }
        if (sectionCode === "variables" && section.entry?.length > 0) {
            outcomeMeasuresReferences = section.entry;
        }
    }
    let groupAssignmentReference;
    if (interventionDescriptionReference && comparatorDescriptionReference) {
        addElementValues.contained = [{
            "resourceType": "EvidenceVariable",
            "id": "group-assignment",
            "meta": { "profile": ["http://hl7.org/fhir/uv/ebm/StructureDefinition/group-assignment"] },
            "title": "GroupAssignment: " + interventionDescriptionReference.display + " vs. " + comparatorDescriptionReference.display,
            "status": "active",
            "definition": {
                "concept": {
                    "coding": [{
                        "system": "https://fevir.net/resources/CodeSystem/179423",
                        "code": "defined-in-handling-and-category",
                        "display": "Defined in handling and category elements"
                    }]
                }
            },
            "handling": "dichotomous",
            "category": [
                {
                    "name": interventionDescriptionReference.display,
                    "valueReference": interventionDescriptionReference
                },
                {
                    "name": comparatorDescriptionReference.display,
                    "valueReference": comparatorDescriptionReference
                }
            ]
        }];
        groupAssignmentReference = {
            "reference": "#group-assignment",
            "type": "EvidenceVariable",
            "display": "GroupAssignment: " + interventionDescriptionReference.display + " vs. " + comparatorDescriptionReference.display
        }
    }
    let groupListSection = {
        "title": "Outcome Group List",
        "code": { "text": "OutcomeGroupList" },
        "text": emptyTextNoData,
        "entry": [],
        "emptyReason": emptyReasonNotStarted
    }
    if (populationReference) {
        groupListSection.entry.push(populationReference);
    }
    if (interventionDescriptionReference) {
        groupListSection.entry.push(interventionDescriptionReference);
    }
    if (comparatorDescriptionReference) {
        groupListSection.entry.push(comparatorDescriptionReference);
    }
    if (groupAssignmentReference) {
        groupListSection.entry.push(groupAssignmentReference);
    }
    if (groupListSection.entry.length > 0) {
        delete groupListSection.emptyReason;
    }
    addElementValues.section.push(groupListSection);
    if (outcomeMeasuresReferences.length > 0) {
        for (const outcomeMeasure of outcomeMeasuresReferences) {
            if (outcomeMeasure.type === "EvidenceVariable") {
                let resultsEntry = {
                    "title": outcomeMeasure.display,
                    "code": {
                        "coding": [{
                            "system": "https://fevir.net/resources/CodeSystem/179423",
                            "code": "summary-of-findings-entry-for-a-single-outcome",
                            "display": "Summary of findings entry for a single outcome"
                        }]
                    },
                    "focus": outcomeMeasure,
                    "text": emptyTextNoData,
                    "section": [
                        {
                            "title": "Outcome Measure",
                            "code": sectionCodeOutcomeMeasure,
                            "text": {
                                "status": "generated",
                                "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\"><p>" + outcomeMeasure.display + "</p></div>"
                            },
                            "entry": [outcomeMeasure]
                        },
                        {
                            "title": "Sample Size",
                            "code": sectionCodeSampleSize,
                            "text": emptyTextNoData,
                            "entry": [],
                            "emptyReason": emptyReasonNotStarted
                        },
                        {
                            "title": "Result Without Treatment",
                            "code": sectionCodeResultWithComparatorAlone,
                            "text": emptyTextNoData,
                            "entry": [],
                            "emptyReason": emptyReasonNotStarted
                        },
                        {
                            "title": "Result With Treatment--Observed",
                            "code": sectionCodeResultWithInterventionAlone,
                            "text": emptyTextNoData,
                            "entry": [],
                            "emptyReason": emptyReasonNotStarted
                        },
                        {
                            "title": "Result With Treatment--Derived",
                            "code": sectionCodeResultWithInterventionAloneCalculated,
                            "text": emptyTextNoData,
                            "entry": [],
                            "emptyReason": emptyReasonNotStarted
                        },
                        {
                            "title": "Effect Estimate",
                            "code": sectionCodeResultWithInterventionVsComparator,
                            "text": emptyTextNoData,
                            "entry": [],
                            "emptyReason": emptyReasonNotStarted
                        },
                        {
                            "title": "Certainty of Evidence",
                            "code": sectionCodeCertaintyOfEvidence,
                            "text": emptyTextNoData,
                            "entry": [],
                            "emptyReason": emptyReasonNotStarted
                        },
                        {
                            "title": "Assertion",
                            "code": sectionCodeAssertion,
                            "text": emptyTextNoData,
                            "entry": [],
                            "emptyReason": emptyReasonNotStarted
                        }
                    ]
                };
                addElementValues.section[1].section.push(resultsEntry);
            }
        }
    }

    return addElementValues;
}

const EditSectionArrayModal = memo(({ editSectionModal,
    setEditSectionModal, sectionState, setSectionState }) => {

    const [titleListState, setTitleListState] = useState([]);
    const [titleListIndexState, setTitleListIndexState] = useState({});
    const [startingTitleListIndexState, setStartingTitleListIndexState] = useState({});
    const [newSectionTitleState, setNewSectionTitleState] = useState({ "title": "", "addSection": false });

    useEffect(() => {
        let startingTitleListIndex = {};
        let startingTitleList = [];
        if (sectionState.section.length) {
            for (const index in sectionState.section) {
                let title = sectionState.section[index].title ||
                    sectionState.section[index].code?.coding?.[0]?.display ||
                    sectionState.section[index].code?.coding?.[0]?.code ||
                    sectionState.section[index].code?.text ||
                    sectionState.section[index].focus?.display ||
                    sectionState.section[index].focus?.reference ||
                    "Untitled Unclassified Section";
                startingTitleListIndex[title] = parseInt(index) + 1;
                startingTitleList.push(title);
            }
        }
        setTitleListState(startingTitleList);
        setTitleListIndexState(startingTitleListIndex);
        setStartingTitleListIndexState(startingTitleListIndex);
    }, [sectionState.section]);

    useEffect(() => {
        if (newSectionTitleState.addSection && newSectionTitleState.title) {
            setTitleListState(prevState => {
                let newTitleList = prevState;
                if (!newTitleList.includes(newSectionTitleState.title)) {
                    newTitleList.push(newSectionTitleState.title);
                }
                return newTitleList;
            });
            setTitleListIndexState(prevState => {
                return { ...prevState, [newSectionTitleState.title]: Object.keys(prevState).length + 1 };
            });
            setNewSectionTitleState(prevState => {
                return { ...prevState, "addSection": false };
            });
        }
    }, [newSectionTitleState]);

    useEffect(() => {
        if (JSON.stringify(titleListIndexState) !== JSON.stringify(startingTitleListIndexState)) {
            let titleList = [];
            for (let title of Object.keys(titleListIndexState)) {
                if (titleListIndexState[title] !== "DELETEME") {
                    titleList.push(title);
                }
            }
            titleList.sort((a, b) => {
                if (titleListIndexState[a] < titleListIndexState[b]) {
                    return -1;
                } else if (titleListIndexState[a] > titleListIndexState[b]) {
                    return 1;
                }
                return 0;
            });
            setSectionState(prevState => {
                let oldSections = prevState.section;
                let sectionDictionary = {};
                for (let section of oldSections) {
                    sectionDictionary[section.title] = section;
                }
                let newSections = [];
                for (let title of titleList) {
                    if (sectionDictionary[title]) {
                        newSections.push(sectionDictionary[title]);
                    } else {
                        newSections.push({ "title": title, "code": { "text": title.replaceAll(" ", "-") } });
                    }
                }
                return { ...prevState, "section": newSections };
            })
        }
    }, [titleListIndexState, startingTitleListIndexState, setSectionState]);

    let modalContent = <div style={{
        paddingTop: "6px", paddingLeft: "20px", paddingRight: "20px",
        paddingBottom: "40px", width: "100%", height: "100%", overflow: "auto"
    }}>
        <Button style={{ padding: "6px", position: "absolute", right: "14px" }}
            className="formButton negative"
            content="✖"
            onClick={() => {
                setEditSectionModal(prevState => { return { ...prevState, openModal: false }; });
            }} />
        {titleListState?.length && titleListState.map((title, titleIndex) => {
            return <div key={titleIndex}>
                <span>
                    <TextField style={{ width: "100%", marginTop: "12px", maxWidth: "60px" }}
                        className="inputField" type='text' inputProps={{ step: "any" }}
                        label={"Index"} size="small" variant='outlined'
                        value={titleListIndexState[title]}
                        onChange={(e) => {
                            if (e.target.value && !isNaN(e.target.value)) {
                                if ((parseInt(e.target.value) !== parseFloat(e.target.value)) || parseInt(e.target.value) < 0) {
                                    alert("Only positive integers are allowed.");
                                }
                            }
                            if (e.target.value === "d" || e.target.value === "D") {
                                setTitleListIndexState(prevState => { return { ...prevState, [title]: "DELETEME" } });
                            } else if (isNaN(e.target.value)) {
                                alert("Only positive integers are allowed, or d or D to delete.");
                            } else if (!isNaN(e.target.value)) {
                                setTitleListIndexState(prevState => {
                                    let oldNumber = prevState[title];
                                    let newNumber = Number(e.target.value);
                                    if (newNumber > oldNumber) {
                                        return { ...prevState, [title]: newNumber + 0.1 };
                                    } else {
                                        return { ...prevState, [title]: newNumber - 0.1 };
                                    }
                                });
                            }
                        }} />
                    {title}
                </span>
            </div>
        })}
        <h2>Add a section</h2>
        <TextField style={{ width: "100%" }} className="inputField"
            type='text' variant='outlined'
            placeholder='Enter section title.' value={newSectionTitleState.title}
            onKeyUp={(event) => {
                if (event.key === 'Enter') {
                    setNewSectionTitleState(prevState => {
                        return {
                            ...prevState,
                            "addSection": true
                        }
                    });
                }
            }}
            onChange={(e) => {
                setNewSectionTitleState(prevState => {
                    return {
                        ...prevState,
                        "title": e.target.value || ""
                    }
                });
            }} />
        <br />
    </div>;

    return <Modal
        style={{ padding: "0px", margin: "0px" }}
        dimmer={<Modal.Dimmer style={{ backgroundColor: "#00000077" }} />}
        open={editSectionModal?.openModal}
        centered={false}
        content={modalContent}
    />;
})

const generateInterventionGroupValues = (referenceMap) => {
    if (referenceMap?.populationReference && referenceMap.interventionDescriptionReference) {
        return {
            "combinationMethod": "all-of",
            "characteristic": [
                {
                    "description": "inclusion in " + referenceMap.populationReference.display,
                    "code": { "text": "Member of" },
                    "valueReference": referenceMap.populationReference,
                    "exclude": false
                },
                {
                    "description": "exposed to " + referenceMap.interventionDescriptionReference.display,
                    "code": { "text": "Exposed to" },
                    "valueReference": referenceMap.interventionDescriptionReference,
                    "exclude": false
                }
            ]
        }
    }
}

const generateComparatorGroupValues = (referenceMap) => {
    if (referenceMap?.populationReference && referenceMap.comparatorDescriptionReference) {
        return {
            "combinationMethod": "all-of",
            "characteristic": [
                {
                    "description": "inclusion in " + referenceMap.populationReference.display,
                    "code": { "text": "Member of" },
                    "valueReference": referenceMap.populationReference,
                    "exclude": false
                },
                {
                    "description": "exposed to " + referenceMap.comparatorDescriptionReference.display,
                    "code": { "text": "Exposed to" },
                    "valueReference": referenceMap.comparatorDescriptionReference,
                    "exclude": false
                }
            ]
        }
    }
}

const generateGroupAssignmentValues = (referenceMap) => {
    if (referenceMap?.interventionDescriptionReference && referenceMap.comparatorDescriptionReference) {
        return {
            "definition": {
                "concept": {
                    "coding": [
                        {
                            "system": "https://fevir.net/resources/CodeSystem/179423",
                            "code": "defined-in-handling-and-category",
                            "display": "Defined in handling and category elements"
                        }
                    ]
                }
            },
            "handling": "dichotomous",
            "category": [
                {
                    "name": referenceMap.interventionDescriptionReference.display || "intervention",
                    "valueReference": referenceMap.interventionDescriptionReference
                },
                {
                    "name": referenceMap.comparatorDescriptionReference.display || "comparator",
                    "valueReference": referenceMap.comparatorDescriptionReference
                }
            ]
        }
    }
}

const generateStartingResultsSection = (referenceMap, reportType) => {
    let startingResultsSection = [];
    let startingGroupListSection = {
        "title": "Group List",
        "code": { "text": "GroupList" },
        "entry": []
    };
    if (referenceMap.populationReference && (reportType === "baseline-measures" || reportType === "participant-flow")) {
        startingGroupListSection.entry.push(referenceMap.populationReference)
    }
    if (referenceMap.screenedGroupReference) {
        startingGroupListSection.entry.push(referenceMap.screenedGroupReference);
    }
    if (referenceMap.enrolledGroupReference) {
        startingGroupListSection.entry.push(referenceMap.enrolledGroupReference);
    }
    if (referenceMap.analyzedGroupReference) {
        startingGroupListSection.entry.push(referenceMap.analyzedGroupReference);
    }
    if (referenceMap.interventionGroupReference || referenceMap.interventionDescriptionReference) {
        startingGroupListSection.entry.push(referenceMap.interventionGroupReference || referenceMap.interventionDescriptionReference);
    }
    if (referenceMap.comparatorGroupReference || referenceMap.comparatorDescriptionReference) {
        startingGroupListSection.entry.push(referenceMap.comparatorGroupReference || referenceMap.comparatorDescriptionReference);
    }
    if (referenceMap.groupAssignmentReference) {
        startingGroupListSection.entry.push(referenceMap.groupAssignmentReference);
    }
    if (startingGroupListSection.entry.length > 0) {
        startingResultsSection.push(startingGroupListSection);
    }
    if (reportType === "baseline-measures" && referenceMap.baselineVariablesListReference) {
        //TODO - generate results section for the Baseline Measures Report
    }
    if (reportType === "participant-flow" && referenceMap.participantFlowVariablesListReference) {
        //TODO - generate results section for the Participant Flow Report
    }
    if (reportType === "outcome-measures") {
        if (referenceMap.outcomeVariablesListReference) {
            //TODO - generate results section for the Outcome Measures Report
        } else if (referenceMap.variablesReferenceList?.length) {
            for (const variable of referenceMap.variablesReferenceList) {
                let startingResultSection = {
                    "title": "Results for " + (variable.display || "[Unnamed variable]"),
                    "code": sectionCodeResults,
                    "focus": variable,
                    "text": emptyTextNoData,
                    "entry": []
                }
                startingResultsSection.push(startingResultSection);
            }
        }
    }

    return startingResultsSection;
}

const generateStartingSummaryOfFindingsSection = (referenceMap) => {
    if (referenceMap) {
        let summaryOfFindingsSectionValues = [
            {
                "title": "Column Headers",
                "code": sectionCodeColumnHeaders,
                "section": [
                    {
                        "title": "Outcome Measure",
                        "code": sectionCodeOutcomeMeasure,
                        "text": {
                            "status": "generated",
                            "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\"><p>Outcome</p></div>"
                        }
                    },
                    {
                        "title": "Sample Size",
                        "code": sectionCodeSampleSize,
                        "text": {
                            "status": "generated",
                            "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\"><p>Sample Size</p></div>"
                        }
                    },
                    {
                        "title": "Result Without Treatment",
                        "code": sectionCodeResultWithComparatorAlone,
                        "text": {
                            "status": "generated",
                            "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\"><p>Result Without Treatment</p></div>"
                        }
                    },
                    {
                        "title": "Result With Treatment--Observed",
                        "code": sectionCodeResultWithInterventionAlone,
                        "text": {
                            "status": "generated",
                            "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\"><p>Result With Treatment (Observed)</p></div>"
                        }
                    },
                    {
                        "title": "Result With Treatment--Derived",
                        "code": sectionCodeResultWithInterventionAloneCalculated,
                        "text": {
                            "status": "generated",
                            "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\"><p>Result With Treatment (Calculated)</p></div>"
                        }
                    },
                    {
                        "title": "Effect Estimate",
                        "code": sectionCodeResultWithInterventionVsComparator,
                        "text": {
                            "status": "generated",
                            "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\"><p>Effect Estimate</p></div>"
                        }
                    },
                    {
                        "title": "Certainty of Evidence",
                        "code": sectionCodeCertaintyOfEvidence,
                        "text": {
                            "status": "generated",
                            "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\"><p>Certainty of Evidence</p></div>"
                        }
                    },
                    {
                        "title": "Assertion",
                        "code": sectionCodeAssertion,
                        "text": {
                            "status": "generated",
                            "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\"><p>What this means</p></div>"
                        }
                    }
                ]
            },
            {
                "title": "Summary of Findings",
                "code": sectionCodeSummaryOfFindings,
                "text": emptyTextNoData,
                "section": [],
                "emptyReason": emptyReasonNotStarted
            },
            {
                "title": "Groups",
                "code": sectionCodeGroups,
                "section": [
                    {
                        "title": "Total Group",
                        "code": sectionCodePopulation,
                        "text": emptyTextNoData,
                        "emptyReason": emptyReasonNotStarted
                    },
                    {
                        "title": "Intervention Group",
                        "code": sectionCodeInterventionGroup,
                        "text": emptyTextNoData,
                        "emptyReason": emptyReasonNotStarted
                    },
                    {
                        "title": "Comparator Group",
                        "code": sectionCodeComparatorGroup,
                        "text": emptyTextNoData,
                        "emptyReason": emptyReasonNotStarted
                    },
                    {
                        "title": "Group Assignment",
                        "code": sectionCodeGroupAssignment,
                        "text": emptyTextNoData,
                        "emptyReason": emptyReasonNotStarted
                    }
                ]
            },
            {
                "title": "Variables (Measures)",
                "code": sectionCodeVariables,
                "text": emptyTextNoData,
                "emptyReason": emptyReasonNotStarted
            }
        ];

        if (referenceMap.populationReference || referenceMap.populationDescriptionGroupReference || referenceMap.synthesisPopulationReference) {
            summaryOfFindingsSectionValues[2].section[0].entry = [referenceMap.populationReference || referenceMap.populationDescriptionGroupReference || referenceMap.synthesisPopulationReference];
            delete summaryOfFindingsSectionValues[0].section[0].emptyReason;
        }
        if (referenceMap.interventionGroupReference || referenceMap.synthesisInterventionDescriptionReference || referenceMap.interventionDescriptionReference) {
            summaryOfFindingsSectionValues[2].section[1].entry = [referenceMap.interventionGroupReference || referenceMap.synthesisInterventionDescriptionReference || referenceMap.interventionDescriptionReference];
            delete summaryOfFindingsSectionValues[0].section[1].emptyReason;
        }
        if (referenceMap.comparatorGroupReference || referenceMap.synthesisComparatorDescriptionReference || referenceMap.comparatorDescriptionReference) {
            summaryOfFindingsSectionValues[2].section[2].entry = [referenceMap.comparatorGroupReference || referenceMap.synthesisComparatorDescriptionReference || referenceMap.comparatorDescriptionReference];
            delete summaryOfFindingsSectionValues[0].section[2].emptyReason;
        }
        if (referenceMap.groupAssignmentReference) {
            summaryOfFindingsSectionValues[2].section[3].entry = [referenceMap.groupAssignmentReference];
            delete summaryOfFindingsSectionValues[0].section[3].emptyReason;
        }
        if (referenceMap.variablesReferenceList) {
            summaryOfFindingsSectionValues[3].entry = referenceMap.variablesReferenceList;
            delete summaryOfFindingsSectionValues[3].emptyReason;
        } else if (referenceMap.outcomeVariablesListReference) {
            summaryOfFindingsSectionValues[3].entry = [referenceMap.outcomeVariablesListReference];
            delete summaryOfFindingsSectionValues[3].emptyReason;
        }

        return summaryOfFindingsSectionValues;
    }
}

const SectionEntryArrayEntry = ({ sectionState, setSectionState, sectionLoaded, foi,
    revisedResourceJson, resourceLoaded }) => {

    let startingResourceType;
    let referenceResourceTypes;
    let selectAnyProfile = true;
    let setProfile;
    let selectProfile;
    let addElementValues = {
        "relatesTo": [
            {
                "type": "part-of",
                "targetReference": {
                    "reference": "Composition/" + foi,
                    "type": "Composition",
                    "display": sectionLoaded.compositionTitle
                }
            }
        ]
    };

    if (sectionLoaded) {
        if (sectionLoaded.sectionCode === "references") {
            startingResourceType = "Citation";
        } else if (sectionLoaded.compositionType === "OutcomeMeasureReport" ||
            sectionLoaded.compositionType === "BaselineMeasureReport") {
            if (sectionLoaded.sectionCode === "results") {
                selectAnyProfile = false;
                selectProfile = ["NonComparativeEvidence", "ComparativeEvidence", "BaselineMeasureEvidence",
                    "InterventionOnlyEvidence", "ComparatorOnlyEvidence", "ComparativeBaselineMeasureEvidence"];
                startingResourceType = "Evidence";
            }
        } else if (sectionLoaded.compositionType === "ParticipantFlowReport") {
            if (sectionLoaded.sectionCode === "results") {
                selectAnyProfile = false;
                selectProfile = ["ParticipantFlowEvidence", "ComparativeParticipantFlowEvidence",
                    "NonComparativeEvidence", "ComparativeEvidence", "InterventionOnlyEvidence", "ComparatorOnlyEvidence"];
                startingResourceType = "Evidence";
            }
        } else if (sectionLoaded.compositionType === "ComparativeEvidenceSynthesisReport") {
            if (sectionLoaded.sectionCode === "population") {
                selectAnyProfile = false;
                selectProfile = ["StudyEligibilityCriteria", "SystematicReviewEligibilityCriteria"];
                startingResourceType = "Group";
                addElementValues = {};
            } else if (sectionLoaded.sectionCode === "intervention-description") {
                referenceResourceTypes = ["Group", "PlanDefinition", "ActivityDefinition"]
                addElementValues = {};
            } else if (sectionLoaded.sectionCode === "comparator-description") {
                referenceResourceTypes = ["Group", "PlanDefinition", "ActivityDefinition"]
                addElementValues = {};
            } else if (sectionLoaded.sectionCode === "research-study") {
                startingResourceType = "ResearchStudy";
            } else if (sectionLoaded.sectionCode === "variables") {
                selectAnyProfile = false;
                selectProfile = ["VariableDefinition", "OutcomeVariablesList"];
                referenceResourceTypes = ["EvidenceVariable", "List"];
                addElementValues = {};
            } else if (sectionLoaded.sectionCode === "results") {
                selectAnyProfile = false;
                setProfile = "OutcomeMeasureReport";
                startingResourceType = "Composition";
                addElementValues = addElementValuesForOutcomeMeasureReportFromComparativeEvidenceSynthesisReport(revisedResourceJson);
            } else if (sectionLoaded.sectionCode === "summary-of-findings") {
                selectAnyProfile = false;
                setProfile = "SummaryOfFindings";
                startingResourceType = "Composition";
                addElementValues = addElementValuesForSummaryOfFindingsFromComparativeEvidenceSynthesisReport(revisedResourceJson);
            }
        } else if (sectionLoaded.compositionType === "ComparativeEvidenceReport") {
            if (sectionLoaded.sectionCode === "population") {
                selectAnyProfile = false;
                setProfile = "StudyGroup";
                startingResourceType = "Group";
                addElementValues = {};
            } else if (sectionLoaded.sectionCode === "screened-group") {
                selectAnyProfile = false;
                setProfile = "StudyGroup";
                startingResourceType = "Group";
                addElementValues = {};
            } else if (sectionLoaded.sectionCode === "enrolled-group") {
                selectAnyProfile = false;
                setProfile = "StudyGroup";
                startingResourceType = "Group";
                addElementValues = {};
            } else if (sectionLoaded.sectionCode === "analyzed-group") {
                selectAnyProfile = false;
                setProfile = "StudyGroup";
                startingResourceType = "Group";
                addElementValues = {};
            } else if (sectionLoaded.sectionCode === "population-definition") {
                selectAnyProfile = false;
                setProfile = "StudyEligibilityCriteria";
                startingResourceType = "Group";
                addElementValues = {};
            } else if (sectionLoaded.sectionCode === "intervention-description") {
                referenceResourceTypes = ["Group", "PlanDefinition", "ActivityDefinition"]
                addElementValues = {};
            } else if (sectionLoaded.sectionCode === "comparator-description") {
                referenceResourceTypes = ["Group", "PlanDefinition", "ActivityDefinition"]
                addElementValues = {};
            } else if (sectionLoaded.sectionCode === "intervention-group") {
                selectAnyProfile = false;
                setProfile = "ExposureGroup";
                startingResourceType = "Group";
                addElementValues = generateInterventionGroupValues(resourceLoaded.resourceReferenceMap) || {};
            } else if (sectionLoaded.sectionCode === "comparator-group") {
                selectAnyProfile = false;
                selectProfile = ["ExposureGroup", "ComparatorGroup"];
                startingResourceType = "Group";
                addElementValues = generateComparatorGroupValues(resourceLoaded.resourceReferenceMap) || {};
            } else if (sectionLoaded.sectionCode === "group-assignment") {
                selectAnyProfile = false;
                setProfile = "GroupAssignment";
                startingResourceType = "EvidenceVariable";
                addElementValues = generateGroupAssignmentValues(resourceLoaded.resourceReferenceMap) || {};
            } else if (sectionLoaded.sectionCode === "research-study") {
                startingResourceType = "ResearchStudy";
            } else if (sectionLoaded.sectionCode === "baseline-measures") {
                selectAnyProfile = false;
                selectProfile = ["BaselineVariablesList", "BaselineMeasureReport"];
                referenceResourceTypes = ["Composition", "List"];
                addElementValues = {
                    "type": compositionTypeCodeBaselineMeasureReport,
                    "relatesTo": [
                        {
                            "type": "part-of",
                            "targetReference": {
                                "reference": "Composition/" + foi,
                                "type": "Composition",
                                "display": sectionLoaded.compositionTitle
                            }
                        }
                    ],
                    "section": generateStartingResultsSection(resourceLoaded.resourceReferenceMap, "baseline-measures") || ""
                };
            } else if (sectionLoaded.sectionCode === "participant-flow") {
                selectAnyProfile = false;
                selectProfile = ["ParticipantFlowVariablesList", "ParticipantFlowReport"];
                referenceResourceTypes = ["Composition", "List"];
                addElementValues = {
                    "type": compositionTypeCodeParticipantFlowReport,
                    "relatesTo": [
                        {
                            "type": "part-of",
                            "targetReference": {
                                "reference": "Composition/" + foi,
                                "type": "Composition",
                                "display": sectionLoaded.compositionTitle
                            }
                        }
                    ],
                    "section": generateStartingResultsSection(resourceLoaded.resourceReferenceMap, "participant-flow") || ""
                };
            } else if (sectionLoaded.sectionCode === "outcome-measures") {
                selectAnyProfile = false;
                selectProfile = ["OutcomeVariablesList", "OutcomeMeasureReport"];
                referenceResourceTypes = ["Composition", "List"];
                addElementValues = {
                    "type": compositionTypeCodeOutcomeMeasureReport,
                    "relatesTo": [
                        {
                            "type": "part-of",
                            "targetReference": {
                                "reference": "Composition/" + foi,
                                "type": "Composition",
                                "display": sectionLoaded.compositionTitle
                            }
                        }
                    ],
                    "section": generateStartingResultsSection(resourceLoaded.resourceReferenceMap, "outcome-measures") || ""
                };
            } else if (sectionLoaded.sectionCode === "summary-of-findings") {
                selectAnyProfile = false;
                selectProfile = ["OutcomeVariablesList", "SummaryOfFindings"];
                referenceResourceTypes = ["Composition", "List"];
                addElementValues = {
                    "type": compositionTypeCodeSummaryOfFindings,
                    "relatesTo": [
                        {
                            "type": "part-of",
                            "targetReference": {
                                "reference": "Composition/" + foi,
                                "type": "Composition",
                                "display": sectionLoaded.compositionTitle
                            }
                        }
                    ],
                    "section": generateStartingSummaryOfFindingsSection(resourceLoaded.resourceReferenceMap) || ""
                };
            }
        } else if (sectionLoaded.compositionType === "Guideline" &&
            sectionLoaded.sectionCode === "recommendations") {
            selectAnyProfile = false;
            setProfile = "Recommendation";
            startingResourceType = "Composition";
        } else if (sectionLoaded.compositionType === "Recommendation") {
            if (sectionLoaded.sectionCode === "ratings" ||
                sectionLoaded.sectionCode === "justification") {
                selectAnyProfile = false;
                setProfile = "RecommendationJustification";
                startingResourceType = "ArtifactAssessment";
                addElementValues = {
                    "artifactReference": {
                        "reference": "Composition/" + foi,
                        "type": "Composition",
                        "display": sectionLoaded.compositionTitle
                    }
                };
            } else if (sectionLoaded.sectionCode === "population") {
                selectAnyProfile = false;
                setProfile = "RecommendationEligibilityCriteria";
                startingResourceType = "Group";
                addElementValues = {};
            } else if (sectionLoaded.sectionCode === "action") {
                startingResourceType = "PlanDefinition";
                addElementValues = {};
            }
        } else if (sectionLoaded.compositionType === "ResearchStudyDataDictionary" &&
            sectionLoaded.sectionCode === "variables") {
            selectAnyProfile = false;
            setProfile = "VariableDefinition";
            startingResourceType = "EvidenceVariable";
        }
    }

    return <>
        <ArrayEntry datatype="Reference" elementName="entry"
            fieldLabel="Resource Reference"
            startingValue={sectionState.entry}
            setResourceState={setSectionState}
            addElementValues={addElementValues}
            deletable deletableArray doNotShowFieldLabel={false}
            startEmptyArrayClosed startCollapsed
            startingResourceType={startingResourceType}
            referenceResourceTypes={referenceResourceTypes}
            selectAnyProfile={selectAnyProfile} setProfile={setProfile}
            selectProfile={selectProfile}
            enableCreation />
    </>
}

const SectionDisplay = ({ sectionPath, foi, resourceLoaded }) => {
    const parentResourceDetail = useSelector(state => selectResourceById(state, foi));
    const initialResourceJson = parentResourceDetail.resourceJson;
    const [sectionLoaded, setSectionLoaded] = useState({});

    const editMode = useSelector(state => selectEditMode(state));
    const [expandState, setExpandState] = useState(false);

    const [editSectionModal, setEditSectionModal] = useState({ "openModal": false });
    const dispatch = useDispatch();
    const resourceChanges = useSelector(state => selectAllResourceChanges(state));
    const resourceCollection = useSelector(state => selectAllResources(state));
    const [sectionState, setSectionState] = useState({});

    useEffect(() => {
        if (!sectionLoaded.compositionTitle && parentResourceDetail?.revisedResourceJson) {
            let fhirJson = parentResourceDetail.revisedResourceJson;
            let sectionPathString;
            let section;
            switch (sectionPath.length) {
                case 0:
                    alert("UNEXPECTED SectionDisplay call for sectionPath length = 0");
                    break;
                case 1:
                    alert("UNEXPECTED SectionDisplay call for sectionPath length = 1");
                    section = fhirJson.section[sectionPath[0]];
                    sectionPathString = "section[" + sectionPath[0] + "]";
                    break;
                case 2:
                    section = fhirJson.section[sectionPath[0]].section[sectionPath[1]];
                    sectionPathString = "section[" + sectionPath[0] + "].section[" + sectionPath[1] + "]";
                    break;
                case 3:
                    section = fhirJson.section[sectionPath[0]].section[sectionPath[1]].section[sectionPath[2]];
                    sectionPathString = "section[" + sectionPath[0] + "].section[" + sectionPath[1] + "].section[" + sectionPath[2] + "]";
                    break;
                case 4:
                    section = fhirJson.section[sectionPath[0]].section[sectionPath[1]].section[sectionPath[2]].section[sectionPath[3]];
                    sectionPathString = "section[" + sectionPath[0] + "].section[" + sectionPath[1] + "].section[" + sectionPath[2] + "].section[" + sectionPath[3] + "]";
                    break;
                case 5:
                    section = fhirJson.section[sectionPath[0]].section[sectionPath[1]].section[sectionPath[2]].section[sectionPath[3]].section[sectionPath[4]];
                    sectionPathString = "section[" + sectionPath[0] + "].section[" + sectionPath[1] + "].section[" + sectionPath[2] + "].section[" + sectionPath[3] + "].section[" + sectionPath[4] + "]";
                    break;
                case 6:
                    section = fhirJson.section[sectionPath[0]].section[sectionPath[1]].section[sectionPath[2]].section[sectionPath[3]].section[sectionPath[4]].section[sectionPath[5]];
                    sectionPathString = "section[" + sectionPath[0] + "].section[" + sectionPath[1] + "].section[" + sectionPath[2] + "].section[" + sectionPath[3] + "].section[" + sectionPath[4] + "].section[" + sectionPath[5] + "]";
                    break;
                default:
                    alert("TOO DEEP SECTION STRUCTURE SectionDisplay call for title editing with sectionPath " + sectionPath.toString());
            }
            let sectionCode = section?.code?.coding?.[0]?.code || section?.code?.text;
            let compositionType = fhirJson.type?.coding?.[0]?.code || fhirJson.type?.text;
            let compositionTitle = fhirJson.title || fhirJson.name;
            let generatedNarrative;
            if (section?.entry?.length === 1 && section.entry[0].reference?.split("/").length === 2 &&
                !isNaN(section.entry[0].reference.split("/")[1])) {
                let resourceFoi = section.entry[0].reference.split("/")[1];
                let resourceDictionary = {};
                for (const entry of resourceCollection) {
                    resourceDictionary[entry.id] = entry;
                }
                if (resourceDictionary[resourceFoi] && resourceDictionary[resourceFoi].revisedResourceJson) {
                    let resourceJson = resourceDictionary[resourceFoi].revisedResourceJson;
                    generatedNarrative = generateResourceTextNarrative(resourceJson);
                }
            }
            setSectionLoaded({
                "section": section, "sectionCode": sectionCode,
                "sectionPathString": sectionPathString, "sectionPath": sectionPath,
                "compositionType": compositionType, "compositionTitle": compositionTitle,
                "generatedNarrative": generatedNarrative
            });
        }
    }, [parentResourceDetail.revisedResourceJson, sectionLoaded.compositionTitle, sectionPath]);

    useEffect(() => {
        if (sectionLoaded.section) {
            let section = sectionLoaded.section;
            setSectionState({
                "editTitle": false,
                "title": section.title || "",
                "text": section.text || emptyTextNoData,
                "entry": section.entry || [],
                "editEmptyReason": false,
                "emptyReason": section.emptyReason || null,
                "section": section?.section || []
            });
        }
    }, [sectionLoaded]);

    useEffect(() => {
        if (editMode && sectionLoaded.section) {
            let newResourceDetail = JSON.parse(JSON.stringify(parentResourceDetail));
            let sectionPathString = "";
            let previousInitialValue = null;
            let previousFinalValue = null;
            switch (sectionPath.length) {
                case 0:
                    alert("UNEXPECTED SectionDisplay call for title editing: sectionPath length = 0");
                    break;
                case 1:
                    alert("UNEXPECTED SectionDisplay call for title editing: sectionPath length = 1");
                    previousInitialValue = initialResourceJson.section[sectionPath[0]].title;
                    previousFinalValue = newResourceDetail.revisedResourceJson.section[sectionPath[0]].title;
                    newResourceDetail.revisedResourceJson.section[sectionPath[0]].title = sectionState.title;
                    sectionPathString = "section[" + sectionPath[0] + "]";
                    break;
                case 2:
                    previousInitialValue = initialResourceJson.section[sectionPath[0]].section[sectionPath[1]].title;
                    previousFinalValue = newResourceDetail.revisedResourceJson.section[sectionPath[0]].section[sectionPath[1]].title;
                    newResourceDetail.revisedResourceJson.section[sectionPath[0]].section[sectionPath[1]].title = sectionState.title;
                    sectionPathString = "section[" + sectionPath[0] + "].section[" + sectionPath[1] + "]";
                    break;
                case 3:
                    previousInitialValue = initialResourceJson.section[sectionPath[0]].section[sectionPath[1]].section[sectionPath[2]].title;
                    previousFinalValue = newResourceDetail.revisedResourceJson.section[sectionPath[0]].section[sectionPath[1]].section[sectionPath[2]].title;
                    newResourceDetail.revisedResourceJson.section[sectionPath[0]].section[sectionPath[1]].section[sectionPath[2]].title = sectionState.title;
                    sectionPathString = "section[" + sectionPath[0] + "].section[" + sectionPath[1] + "].section[" + sectionPath[2] + "]";
                    break;
                case 4:
                    previousInitialValue = initialResourceJson.section[sectionPath[0]].section[sectionPath[1]].section[sectionPath[2]].section[sectionPath[3]].title;
                    previousFinalValue = newResourceDetail.revisedResourceJson.section[sectionPath[0]].section[sectionPath[1]].section[sectionPath[2]].section[sectionPath[3]].title;
                    newResourceDetail.revisedResourceJson.section[sectionPath[0]].section[sectionPath[1]].section[sectionPath[2]].section[sectionPath[3]].title = sectionState.title;
                    sectionPathString = "section[" + sectionPath[0] + "].section[" + sectionPath[1] + "].section[" + sectionPath[2] + "].section[" + sectionPath[3] + "]";
                    break;
                case 5:
                    previousInitialValue = initialResourceJson.section[sectionPath[0]].section[sectionPath[1]].section[sectionPath[2]].section[sectionPath[3]].section[sectionPath[4]].title;
                    previousFinalValue = newResourceDetail.revisedResourceJson.section[sectionPath[0]].section[sectionPath[1]].section[sectionPath[2]].section[sectionPath[3]].section[sectionPath[4]].title;
                    newResourceDetail.revisedResourceJson.section[sectionPath[0]].section[sectionPath[1]].section[sectionPath[2]].section[sectionPath[3]].section[sectionPath[4]].title = sectionState.title;
                    sectionPathString = "section[" + sectionPath[0] + "].section[" + sectionPath[1] + "].section[" + sectionPath[2] + "].section[" + sectionPath[3] + "].section[" + sectionPath[4] + "]";
                    break;
                case 6:
                    previousInitialValue = initialResourceJson.section[sectionPath[0]].section[sectionPath[1]].section[sectionPath[2]].section[sectionPath[3]].section[sectionPath[4]].section[sectionPath[5]].title;
                    previousFinalValue = newResourceDetail.revisedResourceJson.section[sectionPath[0]].section[sectionPath[1]].section[sectionPath[2]].section[sectionPath[3]].section[sectionPath[4]].section[sectionPath[5]].title;
                    newResourceDetail.revisedResourceJson.section[sectionPath[0]].section[sectionPath[1]].section[sectionPath[2]].section[sectionPath[3]].section[sectionPath[4]].section[sectionPath[5]].title = sectionState.title;
                    sectionPathString = "section[" + sectionPath[0] + "].section[" + sectionPath[1] + "].section[" + sectionPath[2] + "].section[" + sectionPath[3] + "].section[" + sectionPath[4] + "].section[" + sectionPath[5] + "]";
                    break;
                default:
                    alert("TOO DEEP SECTION STRUCTURE SectionDisplay call for title editing with sectionPath " + sectionPath.toString());
            }
            if (sectionState.title !== undefined &&
                JSON.stringify(previousFinalValue) !== JSON.stringify(sectionState.title)) {
                console.log("resourceUpdate for Composition.section-path.title");
                dispatch(resourceUpdated(newResourceDetail));
                updateChangeReport(dispatch, resourceChanges, newResourceDetail.id,
                    "string",
                    sectionPathString + ".title",
                    previousInitialValue,
                    sectionState.title);
            }
        }
    }, [sectionState.title]);

    useEffect(() => {
        if (editMode && sectionLoaded.section) {
            let newResourceDetail = JSON.parse(JSON.stringify(parentResourceDetail));
            let sectionPathString = "";
            let previousInitialValue = null;
            let previousFinalValue = null;
            switch (sectionPath.length) {
                case 0:
                    alert("UNEXPECTED SectionDisplay call for text editing: sectionPath length = 0");
                    break;
                case 1:
                    alert("UNEXPECTED SectionDisplay call for text editing: sectionPath length = 1");
                    previousInitialValue = initialResourceJson.section[sectionPath[0]].text;
                    previousFinalValue = JSON.stringify(newResourceDetail.revisedResourceJson.section[sectionPath[0]].text);
                    newResourceDetail.revisedResourceJson.section[sectionPath[0]].text = sectionState.text;
                    sectionPathString = "section[" + sectionPath[0] + "]";
                    break;
                case 2:
                    previousInitialValue = initialResourceJson.section[sectionPath[0]].section[sectionPath[1]].text;
                    previousFinalValue = JSON.stringify(newResourceDetail.revisedResourceJson.section[sectionPath[0]].section[sectionPath[1]].text);
                    newResourceDetail.revisedResourceJson.section[sectionPath[0]].section[sectionPath[1]].text = sectionState.text;
                    sectionPathString = "section[" + sectionPath[0] + "].section[" + sectionPath[1] + "]";
                    break;
                case 3:
                    previousInitialValue = initialResourceJson.section[sectionPath[0]].section[sectionPath[1]].section[sectionPath[2]].text;
                    previousFinalValue = JSON.stringify(newResourceDetail.revisedResourceJson.section[sectionPath[0]].section[sectionPath[1]].section[sectionPath[2]].text);
                    newResourceDetail.revisedResourceJson.section[sectionPath[0]].section[sectionPath[1]].section[sectionPath[2]].text = sectionState.text;
                    sectionPathString = "section[" + sectionPath[0] + "].section[" + sectionPath[1] + "].section[" + sectionPath[2] + "]";
                    break;
                case 4:
                    previousInitialValue = initialResourceJson.section[sectionPath[0]].section[sectionPath[1]].section[sectionPath[2]].section[sectionPath[3]].text;
                    previousFinalValue = JSON.stringify(newResourceDetail.revisedResourceJson.section[sectionPath[0]].section[sectionPath[1]].section[sectionPath[2]].section[sectionPath[3]].text);
                    newResourceDetail.revisedResourceJson.section[sectionPath[0]].section[sectionPath[1]].section[sectionPath[2]].section[sectionPath[3]].text = sectionState.text;
                    sectionPathString = "section[" + sectionPath[0] + "].section[" + sectionPath[1] + "].section[" + sectionPath[2] + "].section[" + sectionPath[3] + "]";
                    break;
                case 5:
                    previousInitialValue = initialResourceJson.section[sectionPath[0]].section[sectionPath[1]].section[sectionPath[2]].section[sectionPath[3]].section[sectionPath[4]].text;
                    previousFinalValue = JSON.stringify(newResourceDetail.revisedResourceJson.section[sectionPath[0]].section[sectionPath[1]].section[sectionPath[2]].section[sectionPath[3]].section[sectionPath[4]].text);
                    newResourceDetail.revisedResourceJson.section[sectionPath[0]].section[sectionPath[1]].section[sectionPath[2]].section[sectionPath[3]].section[sectionPath[4]].text = sectionState.text;
                    sectionPathString = "section[" + sectionPath[0] + "].section[" + sectionPath[1] + "].section[" + sectionPath[2] + "].section[" + sectionPath[3] + "].section[" + sectionPath[4] + "]";
                    break;
                case 6:
                    previousInitialValue = initialResourceJson.section[sectionPath[0]].section[sectionPath[1]].section[sectionPath[2]].section[sectionPath[3]].section[sectionPath[4]].section[sectionPath[5]].text;
                    previousFinalValue = JSON.stringify(newResourceDetail.revisedResourceJson.section[sectionPath[0]].section[sectionPath[1]].section[sectionPath[2]].section[sectionPath[3]].section[sectionPath[4]].section[sectionPath[5]].text);
                    newResourceDetail.revisedResourceJson.section[sectionPath[0]].section[sectionPath[1]].section[sectionPath[2]].section[sectionPath[3]].section[sectionPath[4]].section[sectionPath[5]].text = sectionState.text;
                    sectionPathString = "section[" + sectionPath[0] + "].section[" + sectionPath[1] + "].section[" + sectionPath[2] + "].section[" + sectionPath[3] + "].section[" + sectionPath[4] + "].section[" + sectionPath[5] + "]";
                    break;
                default:
                    alert("TOO DEEP SECTION STRUCTURE SectionDisplay call for text editing with sectionPath " + sectionPath.toString());
            }
            if (sectionState.text !== undefined &&
                previousFinalValue !== JSON.stringify(sectionState.text)) {
                console.log("resourceUpdate for Composition.section-path.text");
                dispatch(resourceUpdated(newResourceDetail));
                setSectionState(prevState => { return { ...prevState, "editEmptyReason": true }; });
                updateChangeReport(dispatch, resourceChanges, newResourceDetail.id,
                    "Narrative",
                    sectionPathString + ".text",
                    previousInitialValue,
                    sectionState.text);
            }
        }
    }, [sectionState.text]);

    useEffect(() => {
        if (editMode && sectionLoaded.section) {
            let newResourceDetail = JSON.parse(JSON.stringify(parentResourceDetail));
            let sectionPathString = "";
            let previousInitialValue = null;
            let previousFinalValue = null;
            switch (sectionPath.length) {
                case 0:
                    alert("UNEXPECTED SectionDisplay call for entry editing: sectionPath length = 0");
                    break;
                case 1:
                    alert("UNEXPECTED SectionDisplay call for entry editing: sectionPath length = 1");
                    previousInitialValue = initialResourceJson.section[sectionPath[0]].entry;
                    previousFinalValue = JSON.stringify(newResourceDetail.revisedResourceJson.section[sectionPath[0]].entry);
                    newResourceDetail.revisedResourceJson.section[sectionPath[0]].entry = sectionState.entry;
                    sectionPathString = "section[" + sectionPath[0] + "]";
                    break;
                case 2:
                    previousInitialValue = initialResourceJson.section[sectionPath[0]].section[sectionPath[1]].entry;
                    previousFinalValue = JSON.stringify(newResourceDetail.revisedResourceJson.section[sectionPath[0]].section[sectionPath[1]].entry);
                    newResourceDetail.revisedResourceJson.section[sectionPath[0]].section[sectionPath[1]].entry = sectionState.entry;
                    sectionPathString = "section[" + sectionPath[0] + "].section[" + sectionPath[1] + "]";
                    break;
                case 3:
                    previousInitialValue = initialResourceJson.section[sectionPath[0]].section[sectionPath[1]].section[sectionPath[2]].entry;
                    previousFinalValue = JSON.stringify(newResourceDetail.revisedResourceJson.section[sectionPath[0]].section[sectionPath[1]].section[sectionPath[2]].entry);
                    newResourceDetail.revisedResourceJson.section[sectionPath[0]].section[sectionPath[1]].section[sectionPath[2]].entry = sectionState.entry;
                    sectionPathString = "section[" + sectionPath[0] + "].section[" + sectionPath[1] + "].section[" + sectionPath[2] + "]";
                    break;
                case 4:
                    previousInitialValue = initialResourceJson.section[sectionPath[0]].section[sectionPath[1]].section[sectionPath[2]].section[sectionPath[3]].entry;
                    previousFinalValue = JSON.stringify(newResourceDetail.revisedResourceJson.section[sectionPath[0]].section[sectionPath[1]].section[sectionPath[2]].section[sectionPath[3]].entry);
                    newResourceDetail.revisedResourceJson.section[sectionPath[0]].section[sectionPath[1]].section[sectionPath[2]].section[sectionPath[3]].entry = sectionState.entry;
                    sectionPathString = "section[" + sectionPath[0] + "].section[" + sectionPath[1] + "].section[" + sectionPath[2] + "].section[" + sectionPath[3] + "]";
                    break;
                case 5:
                    previousInitialValue = initialResourceJson.section[sectionPath[0]].section[sectionPath[1]].section[sectionPath[2]].section[sectionPath[3]].section[sectionPath[4]].entry;
                    previousFinalValue = JSON.stringify(newResourceDetail.revisedResourceJson.section[sectionPath[0]].section[sectionPath[1]].section[sectionPath[2]].section[sectionPath[3]].section[sectionPath[4]].entry);
                    newResourceDetail.revisedResourceJson.section[sectionPath[0]].section[sectionPath[1]].section[sectionPath[2]].section[sectionPath[3]].section[sectionPath[4]].entry = sectionState.entry;
                    sectionPathString = "section[" + sectionPath[0] + "].section[" + sectionPath[1] + "].section[" + sectionPath[2] + "].section[" + sectionPath[3] + "].section[" + sectionPath[4] + "]";
                    break;
                case 6:
                    previousInitialValue = initialResourceJson.section[sectionPath[0]].section[sectionPath[1]].section[sectionPath[2]].section[sectionPath[3]].section[sectionPath[4]].section[sectionPath[5]].entry;
                    previousFinalValue = JSON.stringify(newResourceDetail.revisedResourceJson.section[sectionPath[0]].section[sectionPath[1]].section[sectionPath[2]].section[sectionPath[3]].section[sectionPath[4]].section[sectionPath[5]].entry);
                    newResourceDetail.revisedResourceJson.section[sectionPath[0]].section[sectionPath[1]].section[sectionPath[2]].section[sectionPath[3]].section[sectionPath[4]].section[sectionPath[5]].entry = sectionState.entry;
                    sectionPathString = "section[" + sectionPath[0] + "].section[" + sectionPath[1] + "].section[" + sectionPath[2] + "].section[" + sectionPath[3] + "].section[" + sectionPath[4] + "].section[" + sectionPath[5] + "]";
                    break;
                default:
                    alert("TOO DEEP SECTION STRUCTURE SectionDisplay call for entry editing with sectionPath " + sectionPath.toString());
            }
            if (sectionState.entry !== undefined &&
                previousFinalValue !== JSON.stringify(sectionState.entry)) {
                console.log("resourceUpdate for Composition.section-path.entry");
                dispatch(resourceUpdated(newResourceDetail));
                setSectionState(prevState => { return { ...prevState, "editEmptyReason": true }; });
                updateChangeReport(dispatch, resourceChanges, newResourceDetail.id,
                    "ArrayOfReference",
                    sectionPathString + ".entry",
                    previousInitialValue,
                    sectionState.entry);
            }
        }
    }, [sectionState.entry]);

    useEffect(() => {
        if (editMode && sectionState.editEmptyReason) {
            let sectionIsEmpty = true;
            if (sectionState.section?.length) {
                sectionIsEmpty = false;
            } else if (sectionState.entry?.length) {
                sectionIsEmpty = false;
            } else if (sectionState.text?.status && sectionState.text.status !== "empty") {
                sectionIsEmpty = false;
            }
            if (sectionIsEmpty) {
                setSectionState(prevState => { return { ...prevState, "editEmptyReason": false, "emptyReason": emptyReasonNotStarted }; });
            } else {
                setSectionState(prevState => { return { ...prevState, "editEmptyReason": false, "emptyReason": undefined }; });
            }
        }
    }, [sectionState.section?.length, sectionState.editEmptyReason, sectionState.entry?.length,
    sectionState.text?.status]);

    useEffect(() => {
        if (editMode && sectionLoaded.section) {
            let newResourceDetail = JSON.parse(JSON.stringify(parentResourceDetail));
            let sectionPathString = "";
            let previousInitialValue = null;
            let previousFinalValue = null;
            switch (sectionPath.length) {
                case 0:
                    alert("UNEXPECTED SectionDisplay call for emptyReason editing: sectionPath length = 0");
                    break;
                case 1:
                    alert("UNEXPECTED SectionDisplay call for emptyReason editing: sectionPath length = 1");
                    previousInitialValue = initialResourceJson.section[sectionPath[0]].emptyReason;
                    previousFinalValue = JSON.stringify(newResourceDetail.revisedResourceJson.section[sectionPath[0]].emptyReason);
                    newResourceDetail.revisedResourceJson.section[sectionPath[0]].emptyReason = sectionState.emptyReason;
                    if (!newResourceDetail.resourceJson.section[sectionPath[0]].emptyReason) {
                        delete newResourceDetail.resourceJson.section[sectionPath[0]].emptyReason;
                    }
                    sectionPathString = "section[" + sectionPath[0] + "]";
                    break;
                case 2:
                    previousInitialValue = initialResourceJson.section[sectionPath[0]].section[sectionPath[1]].emptyReason;
                    previousFinalValue = JSON.stringify(newResourceDetail.revisedResourceJson.section[sectionPath[0]].section[sectionPath[1]].emptyReason);
                    newResourceDetail.revisedResourceJson.section[sectionPath[0]].section[sectionPath[1]].emptyReason = sectionState.emptyReason;
                    if (!newResourceDetail.resourceJson.section[sectionPath[0]].section[sectionPath[1]].emptyReason) {
                        delete newResourceDetail.resourceJson.section[sectionPath[0]].section[sectionPath[1]].emptyReason;
                    }
                    sectionPathString = "section[" + sectionPath[0] + "].section[" + sectionPath[1] + "]";
                    break;
                case 3:
                    previousInitialValue = initialResourceJson.section[sectionPath[0]].section[sectionPath[1]].section[sectionPath[2]].emptyReason;
                    previousFinalValue = JSON.stringify(newResourceDetail.revisedResourceJson.section[sectionPath[0]].section[sectionPath[1]].section[sectionPath[2]].emptyReason);
                    newResourceDetail.revisedResourceJson.section[sectionPath[0]].section[sectionPath[1]].section[sectionPath[2]].emptyReason = sectionState.emptyReason;
                    if (!newResourceDetail.resourceJson.section[sectionPath[0]].section[sectionPath[1]].section[sectionPath[2]].emptyReason) {
                        delete newResourceDetail.resourceJson.section[sectionPath[0]].section[sectionPath[1]].section[sectionPath[2]].emptyReason;
                    }
                    sectionPathString = "section[" + sectionPath[0] + "].section[" + sectionPath[1] + "].section[" + sectionPath[2] + "]";
                    break;
                case 4:
                    previousInitialValue = initialResourceJson.section[sectionPath[0]].section[sectionPath[1]].section[sectionPath[2]].section[sectionPath[3]].emptyReason;
                    previousFinalValue = JSON.stringify(newResourceDetail.revisedResourceJson.section[sectionPath[0]].section[sectionPath[1]].section[sectionPath[2]].section[sectionPath[3]].emptyReason);
                    newResourceDetail.revisedResourceJson.section[sectionPath[0]].section[sectionPath[1]].section[sectionPath[2]].section[sectionPath[3]].emptyReason = sectionState.emptyReason;
                    if (!newResourceDetail.resourceJson.section[sectionPath[0]].section[sectionPath[1]].section[sectionPath[2]].section[sectionPath[3]].emptyReason) {
                        delete newResourceDetail.resourceJson.section[sectionPath[0]].section[sectionPath[1]].section[sectionPath[2]].section[sectionPath[3]].emptyReason;
                    }
                    sectionPathString = "section[" + sectionPath[0] + "].section[" + sectionPath[1] + "].section[" + sectionPath[2] + "].section[" + sectionPath[3] + "]";
                    break;
                case 5:
                    previousInitialValue = initialResourceJson.section[sectionPath[0]].section[sectionPath[1]].section[sectionPath[2]].section[sectionPath[3]].section[sectionPath[4]].emptyReason;
                    previousFinalValue = JSON.stringify(newResourceDetail.revisedResourceJson.section[sectionPath[0]].section[sectionPath[1]].section[sectionPath[2]].section[sectionPath[3]].section[sectionPath[4]].emptyReason);
                    newResourceDetail.revisedResourceJson.section[sectionPath[0]].section[sectionPath[1]].section[sectionPath[2]].section[sectionPath[3]].section[sectionPath[4]].emptyReason = sectionState.emptyReason;
                    if (!newResourceDetail.resourceJson.section[sectionPath[0]].section[sectionPath[1]].section[sectionPath[2]].section[sectionPath[3]].section[sectionPath[4]].emptyReason) {
                        delete newResourceDetail.resourceJson.section[sectionPath[0]].section[sectionPath[1]].section[sectionPath[2]].section[sectionPath[3]].section[sectionPath[4]].emptyReason;
                    }
                    sectionPathString = "section[" + sectionPath[0] + "].section[" + sectionPath[1] + "].section[" + sectionPath[2] + "].section[" + sectionPath[3] + "].section[" + sectionPath[4] + "]";
                    break;
                case 6:
                    previousInitialValue = initialResourceJson.section[sectionPath[0]].section[sectionPath[1]].section[sectionPath[2]].section[sectionPath[3]].section[sectionPath[4]].section[sectionPath[5]].emptyReason;
                    previousFinalValue = JSON.stringify(newResourceDetail.revisedResourceJson.section[sectionPath[0]].section[sectionPath[1]].section[sectionPath[2]].section[sectionPath[3]].section[sectionPath[4]].section[sectionPath[5]].emptyReason);
                    newResourceDetail.revisedResourceJson.section[sectionPath[0]].section[sectionPath[1]].section[sectionPath[2]].section[sectionPath[3]].section[sectionPath[4]].section[sectionPath[5]].emptyReason = sectionState.emptyReason;
                    if (!newResourceDetail.resourceJson.section[sectionPath[0]].section[sectionPath[1]].section[sectionPath[2]].section[sectionPath[3]].section[sectionPath[4]].section[sectionPath[5]].emptyReason) {
                        delete newResourceDetail.resourceJson.section[sectionPath[0]].section[sectionPath[1]].section[sectionPath[2]].section[sectionPath[3]].section[sectionPath[4]].section[sectionPath[5]].emptyReason;
                    }
                    sectionPathString = "section[" + sectionPath[0] + "].section[" + sectionPath[1] + "].section[" + sectionPath[2] + "].section[" + sectionPath[3] + "].section[" + sectionPath[4] + "].section[" + sectionPath[5] + "]";
                    break;
                default:
                    alert("TOO DEEP SECTION STRUCTURE SectionDisplay call for emptyReason editing with sectionPath " + sectionPath.toString());
            }
            if (sectionState.emptyReason !== undefined &&
                previousFinalValue !== JSON.stringify(sectionState.emptyReason) &&
                !(!previousFinalValue && !sectionState.emptyReason)) {
                console.log("resourceUpdate for Composition.section-path.emptyReason");
                dispatch(resourceUpdated(newResourceDetail));
                updateChangeReport(dispatch, resourceChanges, newResourceDetail.id,
                    "CodeableConcept",
                    sectionPathString + ".emptyReason",
                    previousInitialValue,
                    sectionState.emptyReason);
            }
        }
    }, [sectionState.emptyReason]);

    useEffect(() => {
        if (editMode && sectionLoaded.section) {
            let newResourceDetail = JSON.parse(JSON.stringify(parentResourceDetail));
            let sectionPathString = "";
            let previousInitialValue = null;
            let previousFinalValue = null;
            switch (sectionPath.length) {
                case 0:
                    alert("UNEXPECTED SectionDisplay call for section editing: sectionPath length = 0");
                    break;
                case 1:
                    alert("UNEXPECTED SectionDisplay call for section editing: sectionPath length = 1");
                    previousInitialValue = initialResourceJson.section[sectionPath[0]].section;
                    previousFinalValue = newResourceDetail.revisedResourceJson.section[sectionPath[0]].section;
                    newResourceDetail.revisedResourceJson.section[sectionPath[0]].section = sectionState.section;
                    sectionPathString = "section[" + sectionPath[0] + "]";
                    break;
                case 2:
                    previousInitialValue = initialResourceJson.section[sectionPath[0]].section[sectionPath[1]].section;
                    previousFinalValue = newResourceDetail.revisedResourceJson.section[sectionPath[0]].section[sectionPath[1]].section;
                    newResourceDetail.revisedResourceJson.section[sectionPath[0]].section[sectionPath[1]].section = sectionState.section;
                    sectionPathString = "section[" + sectionPath[0] + "].section[" + sectionPath[1] + "]";
                    break;
                case 3:
                    previousInitialValue = initialResourceJson.section[sectionPath[0]].section[sectionPath[1]].section[sectionPath[2]].section;
                    previousFinalValue = newResourceDetail.revisedResourceJson.section[sectionPath[0]].section[sectionPath[1]].section[sectionPath[2]].section;
                    newResourceDetail.revisedResourceJson.section[sectionPath[0]].section[sectionPath[1]].section[sectionPath[2]].section = sectionState.section;
                    sectionPathString = "section[" + sectionPath[0] + "].section[" + sectionPath[1] + "].section[" + sectionPath[2] + "]";
                    break;
                case 4:
                    previousInitialValue = initialResourceJson.section[sectionPath[0]].section[sectionPath[1]].section[sectionPath[2]].section[sectionPath[3]].section;
                    previousFinalValue = newResourceDetail.revisedResourceJson.section[sectionPath[0]].section[sectionPath[1]].section[sectionPath[2]].section[sectionPath[3]].section;
                    newResourceDetail.revisedResourceJson.section[sectionPath[0]].section[sectionPath[1]].section[sectionPath[2]].section[sectionPath[3]].section = sectionState.section;
                    sectionPathString = "section[" + sectionPath[0] + "].section[" + sectionPath[1] + "].section[" + sectionPath[2] + "].section[" + sectionPath[3] + "]";
                    break;
                case 5:
                    previousInitialValue = initialResourceJson.section[sectionPath[0]].section[sectionPath[1]].section[sectionPath[2]].section[sectionPath[3]].section[sectionPath[4]].section;
                    previousFinalValue = newResourceDetail.revisedResourceJson.section[sectionPath[0]].section[sectionPath[1]].section[sectionPath[2]].section[sectionPath[3]].section[sectionPath[4]].section;
                    newResourceDetail.revisedResourceJson.section[sectionPath[0]].section[sectionPath[1]].section[sectionPath[2]].section[sectionPath[3]].section[sectionPath[4]].section = sectionState.section;
                    sectionPathString = "section[" + sectionPath[0] + "].section[" + sectionPath[1] + "].section[" + sectionPath[2] + "].section[" + sectionPath[3] + "].section[" + sectionPath[4] + "]";
                    break;
                case 6:
                    previousInitialValue = initialResourceJson.section[sectionPath[0]].section[sectionPath[1]].section[sectionPath[2]].section[sectionPath[3]].section[sectionPath[4]].section[sectionPath[5]].section;
                    previousFinalValue = newResourceDetail.revisedResourceJson.section[sectionPath[0]].section[sectionPath[1]].section[sectionPath[2]].section[sectionPath[3]].section[sectionPath[4]].section[sectionPath[5]].section;
                    newResourceDetail.revisedResourceJson.section[sectionPath[0]].section[sectionPath[1]].section[sectionPath[2]].section[sectionPath[3]].section[sectionPath[4]].section[sectionPath[5]].section = sectionState.section;
                    sectionPathString = "section[" + sectionPath[0] + "].section[" + sectionPath[1] + "].section[" + sectionPath[2] + "].section[" + sectionPath[3] + "].section[" + sectionPath[4] + "].section[" + sectionPath[5] + "]";
                    break;
                default:
                    alert("TOO DEEP SECTION STRUCTURE SectionDisplay call for section editing with sectionPath " + sectionPath.toString());
            }
            if (sectionState.section !== undefined &&
                JSON.stringify(previousFinalValue && previousFinalValue.map(section => section.code)) !== JSON.stringify(sectionState.section.map(section => section.code))
                && !(previousInitialValue === undefined &&
                    JSON.stringify(sectionState.section) === "[]")) {
                console.log("resourceUpdate for Composition.section-path.section");
                dispatch(resourceUpdated(newResourceDetail));
                setSectionState(prevState => { return { ...prevState, "editEmptyReason": true }; });
                updateChangeReport(dispatch, resourceChanges, newResourceDetail.id,
                    "ArrayOfSection",
                    sectionPathString + ".section",
                    previousInitialValue,
                    sectionState.section);
            }
        }
    }, [sectionState.section]);

    return <>{sectionLoaded.section && <div id={sectionLoaded.sectionCode}>
        <div>
            {editMode ?
                <>{
                    sectionState.editTitle ?
                        <>
                            <TextField style={{ width: "100%" }} className="inputField"
                                type='text' variant='outlined'
                                placeholder='Enter section title.' value={sectionState.title}
                                onKeyUp={(event) => {
                                    if (event.key === 'Enter') {
                                        setSectionState(prevState => {
                                            return {
                                                ...prevState,
                                                "editTitle": false
                                            }
                                        });
                                    }
                                }}
                                onChange={(e) => {
                                    setSectionState(prevState => {
                                        return {
                                            ...prevState,
                                            "title": e.target.value || ""
                                        }
                                    });
                                }} />
                            <span className={"unselectable"} style={{ cursor: "pointer", color: "blue" }}
                                onClick={() => { setExpandState(!expandState); }}
                            >{expandState ? " ▼ collapse" : " ► expand"}
                            </span>
                        </>
                        :
                        <h3>
                            <span>{sectionState.title || sectionLoaded.section.title || "Untitled Section"}</span>
                            <span>  <button type="button" style={{ marginLeft: "12px" }}
                                onClick={() => {
                                    setSectionState(prevState => {
                                        return {
                                            ...prevState,
                                            "editTitle": true
                                        }
                                    });
                                }} >✎ Edit title</button>
                            </span>
                            <span className={"unselectable"} style={{ cursor: "pointer", color: "blue" }}
                                onClick={() => { setExpandState(!expandState); }}
                            >{expandState ? " ▼ collapse" : " ► expand"}
                            </span>
                        </h3>
                }</>
                :
                <h3>
                    {sectionState.title || sectionLoaded.section.title || "Untitled Section"}
                    <span className={"unselectable"} style={{ cursor: "pointer", color: "blue" }}
                        onClick={() => { setExpandState(!expandState); }}
                    >{expandState ? " ▼ collapse" : " ► expand"}
                    </span>
                </h3>
            }
            {expandState && <div style={{ marginLeft: "24px" }}>
                <div>
                    {editMode ?
                        <NarrativeEntry elementName="text"
                            startingValue={sectionState.text}
                            generatedNarrative={sectionLoaded.generatedNarrative}
                            setResourceState={setSectionState} />
                        :
                        <>{(sectionLoaded.section.text?.div &&
                            sectionLoaded.section.text.div !== "<div xmlns=\"http://www.w3.org/1999/xhtml\">[No data.]</div>" &&
                            sectionLoaded.section.text.status !== "empty") &&
                            <ReactMarkdown children={sectionState?.text?.div || sectionLoaded.section.text.div} rehypePlugins={[rehypeRaw]} />}</>
                    }
                </div>
                {editMode ?
                    <SectionEntryArrayEntry sectionState={sectionState}
                        setSectionState={setSectionState} sectionLoaded={sectionLoaded}
                        foi={foi} revisedResourceJson={parentResourceDetail?.revisedResourceJson}
                        resourceLoaded={resourceLoaded} />
                    :
                    <>{(sectionLoaded.section.entry?.length > 0) && sectionLoaded.section.entry.map((entry, entryIndex) => {
                        return <span key={entryIndex}>
                            <p>
                                <DisplayFromFHIRReference reference={entry} />
                            </p>
                        </span>
                    })}</>
                }
                {editMode && <div>
                    <Button className="formButton" style={{ margin: "0px" }}
                        content="Add/Delete/Reorganize Sections" compact
                        onClick={() => { setEditSectionModal({ "openModal": true }) }} />
                    <br />
                </div>}
                {editSectionModal.openModal && <EditSectionArrayModal editSectionModal={editSectionModal}
                    setEditSectionModal={setEditSectionModal}
                    sectionState={sectionState}
                    setSectionState={setSectionState} />}
                {(sectionLoaded.section.section?.length > 0) && sectionLoaded.section.section.map((subsection, subsectionIndex) => {
                    return <div key={subsectionIndex}>
                        <SectionDisplay sectionPath={sectionPath.concat([subsectionIndex])}
                            foi={foi} />
                    </div>
                })}
                {editMode ?
                    <>
                        <CodeableConceptEntry elementName="emptyReason" fieldLabel="Empty Reason"
                            startingValue={sectionState.emptyReason}
                            codeableConceptLevelValueSet={emptyReasonCodeableConceptSet}
                            setResourceState={setSectionState} deletable startCollapsed />
                    </>
                    :
                    <>{sectionLoaded.section.emptyReason && <div>
                        <p>Section is empty: {sectionState?.emptyReason ?
                            <>{sectionState.emptyReason.coding?.[0]?.display} {sectionState.emptyReason.text}</>
                            :
                            <>{sectionLoaded.section.emptyReason.coding?.[0]?.display} {sectionLoaded.section.emptyReason.text}</>}
                        </p>
                    </div>}</>}
            </div>}
        </div>
    </div>}</>
}

const TopSectionDisplay = ({ foi, sectionIndex, resourceLoaded }) => {
    const parentResourceDetail = useSelector(state => selectResourceById(state, foi));
    const initialResourceJson = parentResourceDetail.resourceJson;
    const calculateSectionPath = (sectionIndex) => { return [sectionIndex]; };
    const sectionPath = useMemo(() => calculateSectionPath(sectionIndex), [sectionIndex]);
    const [sectionLoaded, setSectionLoaded] = useState({});
    const editMode = useSelector(state => selectEditMode(state));

    const [sectionState, setSectionState] = useState({});

    const [editSectionModal, setEditSectionModal] = useState({ "openModal": false });
    const dispatch = useDispatch();
    const resourceChanges = useSelector(state => selectAllResourceChanges(state));
    const resourceCollection = useSelector(state => selectAllResources(state));

    useEffect(() => {
        if ((!sectionLoaded.compositionTitle && parentResourceDetail?.revisedResourceJson) ||
            (!editMode && parentResourceDetail?.revisedResourceJson)) {
            let fhirJson = parentResourceDetail.revisedResourceJson;
            let section = fhirJson.section[sectionIndex];
            let sectionCode = section?.code?.coding?.[0]?.code || section?.code?.text;
            let compositionType = fhirJson.type?.coding?.[0]?.code || fhirJson.type?.text;
            let compositionTitle = fhirJson.title || fhirJson.name;
            let sectionFocus = section?.focus?.reference;
            let sectionPointer = sectionFocus || sectionCode;
            let generatedNarrative;
            if (section?.entry?.length === 1 && section.entry[0].reference?.split("/").length === 2 &&
                !isNaN(section.entry[0].reference.split("/")[1])) {
                let resourceFoi = section.entry[0].reference.split("/")[1];
                let resourceDictionary = {};
                for (const entry of resourceCollection) {
                    resourceDictionary[entry.id] = entry;
                }
                if (resourceDictionary[resourceFoi] && resourceDictionary[resourceFoi].resourceJson) {
                    let resourceJson = resourceDictionary[resourceFoi].resourceJson;
                    generatedNarrative = generateResourceTextNarrative(resourceJson);
                }
            }
            setSectionLoaded({
                "section": section, "sectionCode": sectionCode, "sectionPointer": sectionPointer,
                "compositionType": compositionType, "compositionTitle": compositionTitle,
                "generatedNarrative": generatedNarrative
            });
        }
    }, [parentResourceDetail, sectionIndex, sectionLoaded.compositionTitle, editMode]);

    useEffect(() => {
        if (sectionLoaded.section) {
            let section = sectionLoaded.section;
            setSectionState({
                "editTitle": false,
                "title": section.title || "",
                "text": section.text || emptyTextNoData,
                "entry": section.entry || [],
                "editEmptyReason": false,
                "emptyReason": section.emptyReason || null,
                "section": section?.section || []
            });
        }
    }, [sectionLoaded.section]);

    useEffect(() => {
        if (editMode && sectionLoaded.section) {
            let newResourceDetail = JSON.parse(JSON.stringify(parentResourceDetail));
            let previousInitialValue = null;
            let previousFinalValue = null;
            previousInitialValue = initialResourceJson.section?.[sectionPath[0]]?.title || null;
            previousFinalValue = newResourceDetail.revisedResourceJson.section?.[sectionPath[0]]?.title || null;
            newResourceDetail.revisedResourceJson.section[sectionPath[0]].title = sectionState.title;
            if (sectionState.title !== undefined &&
                JSON.stringify(previousFinalValue) !== JSON.stringify(sectionState.title)) {
                console.log("resourceUpdate for Composition.section[].title");
                dispatch(resourceUpdated(newResourceDetail));
                updateChangeReport(dispatch, resourceChanges, newResourceDetail.id,
                    "string",
                    "section[" + sectionPath[0] + "].title",
                    previousInitialValue,
                    sectionState.title);
            }
        }
    }, [sectionState.title]);

    useEffect(() => {
        if (editMode && sectionLoaded.section) {
            let newResourceDetail = JSON.parse(JSON.stringify(parentResourceDetail));
            let previousInitialValue = null;
            let previousFinalValue = null;
            previousInitialValue = initialResourceJson.section?.[sectionPath[0]]?.text || null;
            previousFinalValue = JSON.stringify(newResourceDetail.revisedResourceJson.section?.[sectionPath[0]]?.text || null);
            newResourceDetail.revisedResourceJson.section[sectionPath[0]].text = sectionState.text;

            if (sectionState.text !== undefined &&
                previousFinalValue !== JSON.stringify(sectionState.text)) {
                console.log("resourceUpdate for Composition.section[].text");
                dispatch(resourceUpdated(newResourceDetail));
                setSectionState(prevState => { return { ...prevState, "editEmptyReason": true }; });
                updateChangeReport(dispatch, resourceChanges, newResourceDetail.id,
                    "Narrative",
                    "section[" + sectionPath[0] + "].text",
                    previousInitialValue,
                    sectionState.text);
            }
        }
    }, [sectionState.text]);

    useEffect(() => {
        if (editMode && sectionLoaded.section) {
            let newResourceDetail = JSON.parse(JSON.stringify(parentResourceDetail));
            let previousInitialValue = null;
            let previousFinalValue = null;
            previousInitialValue = initialResourceJson.section?.[sectionPath[0]]?.entry || null;
            previousFinalValue = JSON.stringify(newResourceDetail.revisedResourceJson.section?.[sectionPath[0]]?.entry || null);
            newResourceDetail.revisedResourceJson.section[sectionPath[0]].entry = sectionState.entry;
            if (sectionState.entry !== undefined &&
                previousFinalValue !== JSON.stringify(sectionState.entry)) {
                console.log("resourceUpdate for Composition.section[].entry");
                dispatch(resourceUpdated(newResourceDetail));
                setSectionState(prevState => { return { ...prevState, "editEmptyReason": true }; });
                updateChangeReport(dispatch, resourceChanges, newResourceDetail.id,
                    "ArrayOfReference",
                    "section[" + sectionPath[0] + "].entry",
                    previousInitialValue,
                    sectionState.entry);
            }
        }
    }, [sectionState.entry]);

    useEffect(() => {
        if (editMode && sectionState.editEmptyReason) {
            let sectionIsEmpty = true;
            if (sectionState.section?.length) {
                sectionIsEmpty = false;
            } else if (sectionState.entry?.length) {
                sectionIsEmpty = false;
            } else if (sectionState.text?.status && sectionState.text.status !== "empty") {
                sectionIsEmpty = false;
            }
            if (sectionIsEmpty) {
                setSectionState(prevState => { return { ...prevState, "editEmptyReason": false, "emptyReason": emptyReasonNotStarted }; });
            } else {
                setSectionState(prevState => { return { ...prevState, "editEmptyReason": false, "emptyReason": undefined }; });
            }
        }
    }, [sectionState.editEmptyReason, sectionState.entry?.length, sectionState.section?.length,
    sectionState.text?.status]);

    useEffect(() => {
        if (editMode && sectionLoaded.section) {
            let newResourceDetail = JSON.parse(JSON.stringify(parentResourceDetail));
            let previousInitialValue = null;
            let previousFinalValue = null;
            previousInitialValue = initialResourceJson.section?.[sectionPath[0]]?.emptyReason || null;
            previousFinalValue = JSON.stringify(newResourceDetail.revisedResourceJson.section?.[sectionPath[0]]?.emptyReason || null);
            newResourceDetail.revisedResourceJson.section[sectionPath[0]].emptyReason = sectionState.emptyReason;
            if (newResourceDetail.resourceJson.section[sectionPath[0]] &&
                !newResourceDetail.resourceJson.section[sectionPath[0]].emptyReason) {
                delete newResourceDetail.resourceJson.section[sectionPath[0]].emptyReason;
            }
            if (sectionState.emptyReason !== undefined &&
                previousFinalValue !== JSON.stringify(sectionState.emptyReason) &&
                !(!previousFinalValue && !sectionState.emptyReason)) {
                console.log("resourceUpdate for Composition.section[].emptyReason");
                dispatch(resourceUpdated(newResourceDetail));
                updateChangeReport(dispatch, resourceChanges, newResourceDetail.id,
                    "CodeableConcept",
                    "section[" + sectionPath[0] + "].emptyReason",
                    previousInitialValue,
                    sectionState.emptyReason);
            }
        }
    }, [sectionState.emptyReason]);

    useEffect(() => {
        if (editMode && sectionLoaded.section) {
            let newResourceDetail = JSON.parse(JSON.stringify(parentResourceDetail));
            let previousInitialValue = null;
            let previousFinalValue = null;
            previousInitialValue = initialResourceJson.section?.[sectionPath[0]]?.section || null;
            previousFinalValue = newResourceDetail.revisedResourceJson.section?.[sectionPath[0]]?.section || null;
            newResourceDetail.revisedResourceJson.section[sectionPath[0]].section = sectionState.section;
            if (sectionState.section !== undefined &&
                JSON.stringify(previousFinalValue && previousFinalValue.map(section => section.code)) !== JSON.stringify(sectionState.section.map(section => section.code))
                && !(previousInitialValue === undefined &&
                    JSON.stringify(sectionState.section) === "[]")) {
                console.log("resourceUpdate for Composition.section[].section");
                dispatch(resourceUpdated(newResourceDetail));
                setSectionState(prevState => { return { ...prevState, "editEmptyReason": true }; });
                updateChangeReport(dispatch, resourceChanges, newResourceDetail.id,
                    "ArrayOfSection",
                    "section[" + sectionPath[0] + "].section",
                    previousInitialValue,
                    sectionState.section);
            }
        }
    }, [sectionState.section]);

    return <>{sectionLoaded.section && <div id={sectionLoaded.sectionPointer}>
        {editMode ?
            <>
                {sectionState.editTitle ?
                    <>
                        <TextField style={{ width: "100%" }} className="inputField"
                            type='text' variant='outlined'
                            placeholder='Enter section title.' value={sectionState.title}
                            onKeyUp={(event) => {
                                if (event.key === 'Enter') {
                                    setSectionState(prevState => {
                                        return {
                                            ...prevState,
                                            "editTitle": false
                                        }
                                    });
                                }
                            }}
                            onChange={(e) => {
                                setSectionState(prevState => {
                                    return {
                                        ...prevState,
                                        "title": e.target.value || ""
                                    }
                                });
                            }} />
                    </>
                    :
                    <h2>
                        <span>{sectionState.title || sectionLoaded.section.title || sectionLoaded.sectionCode + " section" || "Untitled Section"}</span>
                        <span>  <button type="button" style={{ marginLeft: "12px" }}
                            onClick={() => {
                                setSectionState(prevState => {
                                    return {
                                        ...prevState,
                                        "editTitle": true
                                    }
                                });
                            }} >✎ Edit title</button></span>
                    </h2>
                }</>
            :
            <h2>{sectionState.title || sectionLoaded.section.title || sectionLoaded.sectionCode + " section" || "Untitled Section"}</h2>
        }
        <div style={{ marginLeft: "24px" }}>
            <div>
                {editMode ?
                    <NarrativeEntry elementName="text"
                        startingValue={sectionState.text}
                        generatedNarrative={sectionLoaded.generatedNarrative}
                        setResourceState={setSectionState} />
                    :
                    <>{(sectionLoaded.section.text?.div &&
                        sectionLoaded.section.text.div !== "<div xmlns=\"http://www.w3.org/1999/xhtml\">[No data.]</div>" &&
                        sectionLoaded.section.text.status !== "empty") &&
                        <ReactMarkdown children={sectionState?.text?.div || sectionLoaded.section.text.div} rehypePlugins={[rehypeRaw]} />}</>
                }
            </div>
            {editMode ?
                <SectionEntryArrayEntry sectionState={sectionState}
                    setSectionState={setSectionState} sectionLoaded={sectionLoaded}
                    foi={foi} revisedResourceJson={parentResourceDetail?.revisedResourceJson}
                    resourceLoaded={resourceLoaded} />
                :
                <>{(sectionLoaded.section.entry?.length > 0) && sectionLoaded.section.entry.map((entry, entryIndex) => {
                    return <span key={entryIndex}>
                        <p>
                            <DisplayFromFHIRReference reference={entry} />
                        </p>
                    </span>
                })}</>
            }
            {editMode && <div>
                <Button className="formButton" style={{ margin: "0px" }}
                    content="Add/Delete/Reorganize Sections" compact
                    onClick={() => { setEditSectionModal({ "openModal": true }) }} />
                <br />
            </div>}
            {editSectionModal.openModal && <EditSectionArrayModal editSectionModal={editSectionModal}
                setEditSectionModal={setEditSectionModal}
                sectionState={sectionState}
                setSectionState={setSectionState} />}
            {(sectionLoaded.section.section?.length > 0) && sectionLoaded.section.section.map((subsection, subsectionIndex) => {
                return <div key={subsectionIndex}>
                    <SectionDisplay sectionPath={sectionPath.concat([subsectionIndex])}
                        foi={foi} resourceLoaded={resourceLoaded} />
                </div>
            })}
            {editMode ?
                <>
                    <CodeableConceptEntry elementName="emptyReason" fieldLabel="Empty Reason"
                        startingValue={sectionState.emptyReason}
                        codeableConceptLevelValueSet={emptyReasonCodeableConceptSet}
                        setResourceState={setSectionState} deletable startCollapsed />
                </>
                :
                <>{sectionLoaded.section.emptyReason && <div>
                    <p>Section is empty: {sectionState?.emptyReason ?
                        <>{sectionState.emptyReason.coding?.[0]?.display} {sectionState.emptyReason.text}</>
                        :
                        <>{sectionLoaded.section.emptyReason.coding?.[0]?.display} {sectionLoaded.section.emptyReason.text}</>
                    }
                    </p>
                </div>}</>}
        </div>
    </div>}</>
}

const TopSectionNavigation = ({ sections, outcomeMeasureReport, hasResourceCollection }) => {
    const navigate = useNavigate();
    return <div className={`maxNavigationSegment unselectable`} style={{lineHeight: "1"}}>
        <div style={{marginBottom: "8px"}}>
            <span style={{ cursor: "pointer", color: "blue" }} onClick={() => {
                navigate("#top");
                scrollTo(null, "top", true, "navbar");
            }}
            >Summary (not a section)</span>
        </div>
        {outcomeMeasureReport && <div style={{marginBottom: "8px"}}>
            <span style={{ cursor: "pointer", color: "blue" }} onClick={() => {
                navigate("#evidenceTable");
                scrollTo(null, "evidenceTable", true, "navbar");
            }}
            >Evidence Table</span>
        </div>}
        {sections.map((section, sectionIndex) => {
            let sectionCode = section?.code?.coding?.[0]?.code || section?.code?.text;
            let sectionFocus = section?.focus?.reference;
            let sectionPointer = sectionFocus || sectionCode;
            let title = section.title || sectionCode + " section" || "Untitled Section";
            return <div key={sectionIndex} style={{marginBottom: "8px"}}>
                <span style={{ cursor: "pointer", color: "blue" }} onClick={() => {
                    navigate("#" + sectionPointer);
                    scrollTo(null, sectionPointer, true, "navbar");
                }}
                >{title}</span>
            </div>
        })}
        <div>
            <span style={{ cursor: "pointer", color: "blue" }}
                onClick={() => {
                    navigate("#metadata");
                    scrollTo(null, "metadata", true, "navbar");
                }}>Metadata</span>
        </div>
        {hasResourceCollection && <div>
            <span style={{ cursor: "pointer", color: "blue" }}
                onClick={() => {
                    navigate("#resource-collection");
                    scrollTo(null, "resource-collection", true, "navbar");
                }}>Resource Collection</span>
        </div>}
    </div>
}

const displayProfileName = (profile, resourceType) => {
    let profileName;
    const profileDictionary = ProfilesByResourceType[resourceType];
    for (const key in profileDictionary) {
        if (profileDictionary[key].url === profile) {
            profileName = key;
        }
    }
    return profileName || profile;
}

const MetadataDisplay = ({ fhirJson, parentResourceDetail }) => {
    const editMode = useSelector(state => selectEditMode(state));
    const initialResourceJson = parentResourceDetail.resourceJson;
    let additionalLanguages = [];
    let extendedContactDetail = [];
    let versionAlgorithmString = "";
    let description = "";
    let copyright = "";
    let copyrightLabel = "";
    let approvalDate = "";
    let lastReviewDate = "";
    let publicationDate = "";
    let effectivePeriod = "";
    if (Array.isArray(fhirJson.extension) && fhirJson.extension.length > 0) {
        for (const extension of fhirJson.extension) {
            switch (extension.url) {
                case 'http://hl7.org/fhir/uv/ebm/StructureDefinition/additional-language':
                    additionalLanguages.push(extension.valueCode);
                    break;
                case 'http://hl7.org/fhir/StructureDefinition/artifact-extended-contact-detail':
                    extendedContactDetail.push(extension.valueExtendedContactDetail);
                    break;
                case 'http://hl7.org/fhir/StructureDefinition/artifact-versionAlgorithm':
                    versionAlgorithmString = extension.valueString;
                    break;
                case 'http://hl7.org/fhir/StructureDefinition/artifact-description':
                    description = extension.valueMarkdown;
                    break;
                case 'http://hl7.org/fhir/StructureDefinition/artifact-copyright':
                    copyright = extension.valueMarkdown;
                    break;
                case 'http://hl7.org/fhir/StructureDefinition/artifact-copyrightLabel':
                    copyrightLabel = extension.valueString;
                    break;
                case 'http://hl7.org/fhir/StructureDefinition/artifact-approvalDate':
                    approvalDate = extension.valueDate;
                    break;
                case 'http://hl7.org/fhir/StructureDefinition/artifact-lastReviewDate':
                    lastReviewDate = extension.valueDate;
                    break;
                case 'http://hl7.org/fhir/StructureDefinition/artifact-publicationDate':
                    publicationDate = extension.valueDate;
                    break;
                case 'http://hl7.org/fhir/StructureDefinition/artifact-effectivePeriod':
                    effectivePeriod = extension.valuePeriod;
                    break;
                default:
                //
            }
        }
    }

    const [metadataState, setMetadataState] = useState({});
    const [extensionState, setExtensionState] = useState(null);
    const [startingExtensionState, setStartingExtensionState] = useState(null);
    const dispatch = useDispatch();
    const resourceChanges = useSelector(state => selectAllResourceChanges(state));

    useEffect(() => {
        if (editMode && fhirJson) {
            setMetadataState({
                "loaded": true,
                "title": fhirJson.title || "",
                "identifier": fhirJson.identifier || [],
                "metaProfile": fhirJson.meta.profile || [],
                "language": fhirJson.language || "",
                "version": fhirJson.version || "",
                "status": fhirJson.status || "",
                "date": fhirJson.date || "",
                "custodian": fhirJson.custodian || "",
                "useContext": fhirJson.useContext || [],
                "note": fhirJson.note || [],
                "author": fhirJson.author || [],
                "attester": fhirJson.attester || [],
                "relatesTo": fhirJson.relatesTo || []
            });
        }
    }, [editMode, fhirJson]);

    useEffect(() => {
        let startingExtension = fhirJson.extension || [];
        let startingExtensionStateValue = { "otherExtensions": [], "additionalLanguage": [], "extendedContactDetail": [] }
        if (Array.isArray(startingExtension) && startingExtension.length > 0) {
            for (const extension of startingExtension) {
                switch (extension.url) {
                    case 'http://hl7.org/fhir/uv/ebm/StructureDefinition/additional-language':
                        startingExtensionStateValue.additionalLanguage.push(extension.valueCode);
                        break;
                    case 'http://hl7.org/fhir/StructureDefinition/artifact-extended-contact-detail':
                        startingExtensionStateValue.extendedContactDetail.push(extension.valueExtendedContactDetail);
                        break;
                    case 'http://hl7.org/fhir/StructureDefinition/artifact-versionAlgorithm':
                        startingExtensionStateValue.versionAlgorithmString = extension.valueString;
                        break;
                    case 'http://hl7.org/fhir/StructureDefinition/artifact-description':
                        startingExtensionStateValue.description = extension.valueMarkdown;
                        break;
                    case 'http://hl7.org/fhir/StructureDefinition/artifact-copyright':
                        startingExtensionStateValue.copyright = extension.valueMarkdown;
                        break;
                    case 'http://hl7.org/fhir/StructureDefinition/artifact-copyrightLabel':
                        startingExtensionStateValue.copyrightLabel = extension.valueString;
                        break;
                    case 'http://hl7.org/fhir/StructureDefinition/artifact-approvalDate':
                        startingExtensionStateValue.approvalDate = extension.valueDate;
                        break;
                    case 'http://hl7.org/fhir/StructureDefinition/artifact-lastReviewDate':
                        startingExtensionStateValue.lastReviewDate = extension.valueDate;
                        break;
                    case 'http://hl7.org/fhir/StructureDefinition/artifact-publicationDate':
                        startingExtensionStateValue.publicationDate = extension.valueDate;
                        break;
                    case 'http://hl7.org/fhir/StructureDefinition/artifact-effectivePeriod':
                        startingExtensionStateValue.effectivePeriod = extension.valuePeriod;
                        break;
                    default:
                        startingExtensionStateValue.otherExtensions.push(extension)
                }
            }
        }
        setExtensionState(JSON.parse(JSON.stringify(startingExtensionStateValue)));
        setStartingExtensionState(JSON.parse(JSON.stringify(startingExtensionStateValue)));
    }, [fhirJson.extension])

    useEffect(() => {
        if (editMode && extensionState && JSON.stringify(extensionState) !== JSON.stringify(startingExtensionState)) {
            let newExtension = [];
            if (extensionState.otherExtensions) {
                newExtension = JSON.parse(JSON.stringify(extensionState.otherExtensions));
            }
            if (extensionState.additionalLanguage) {
                for (let code of extensionState.additionalLanguage) {
                    newExtension.push({
                        "url": "http://hl7.org/fhir/uv/ebm/StructureDefinition/additional-language",
                        "valueCode": code
                    });
                }
            }
            if (extensionState.extendedContactDetail) {
                for (let extendedContactDetail of extensionState.extendedContactDetail) {
                    newExtension.push({
                        "url": "http://hl7.org/fhir/StructureDefinition/artifact-extended-contact-detail",
                        "valueExtendedContactDetail": extendedContactDetail
                    });
                }
            }
            if (extensionState.versionAlgorithmString) {
                newExtension.push({
                    "url": "http://hl7.org/fhir/StructureDefinition/artifact-versionAlgorithm",
                    "valueString": extensionState.versionAlgorithmString
                });
            }
            if (extensionState.description) {
                newExtension.push({
                    "url": "http://hl7.org/fhir/StructureDefinition/artifact-description",
                    "valueMarkdown": extensionState.description
                });
            }
            if (extensionState.copyright) {
                newExtension.push({
                    "url": "http://hl7.org/fhir/StructureDefinition/artifact-copyright",
                    "valueMarkdown": extensionState.copyright
                });
            }
            if (extensionState.copyrightLabel) {
                newExtension.push({
                    "url": "http://hl7.org/fhir/StructureDefinition/artifact-copyrightLabel",
                    "valueString": extensionState.copyrightLabel
                });
            }
            if (extensionState.approvalDate) {
                newExtension.push({
                    "url": "http://hl7.org/fhir/StructureDefinition/artifact-approvalDate",
                    "valueDate": extensionState.approvalDate
                });
            }
            if (extensionState.lastReviewDate) {
                newExtension.push({
                    "url": "http://hl7.org/fhir/StructureDefinition/artifact-lastReviewDate",
                    "valueDate": extensionState.lastReviewDate
                });
            }
            if (extensionState.publicationDate) {
                newExtension.push({
                    "url": "http://hl7.org/fhir/StructureDefinition/artifact-publicationDate",
                    "valueDate": extensionState.publicationDate
                });
            }
            if (extensionState.effectivePeriod) {
                newExtension.push({
                    "url": "http://hl7.org/fhir/StructureDefinition/artifact-effectivePeriod",
                    "valuePeriod": extensionState.effectivePeriod
                });
            }
            if (!(newExtension.length > 0)) {
                newExtension = null;
            }
            let newResourceDetail = JSON.parse(JSON.stringify(parentResourceDetail));
            if (JSON.stringify(newResourceDetail.revisedResourceJson.extension) !== JSON.stringify(newExtension)) {
                newResourceDetail.revisedResourceJson.extension = newExtension;
                console.log("resourceUpdate for Composition.extension");
                dispatch(resourceUpdated(newResourceDetail));
                updateChangeReport(dispatch, resourceChanges, newResourceDetail.id,
                    "ArrayOfExtension",
                    "extension",
                    startingExtensionState,
                    extensionState);
            }
        }
    }, [extensionState])

    useEffect(() => {
        if (editMode && metadataState.loaded) {
            let newResourceDetail = JSON.parse(JSON.stringify(parentResourceDetail));
            let previousInitialValue = null;
            let previousFinalValue = null;
            previousInitialValue = initialResourceJson.title;
            previousFinalValue = newResourceDetail.revisedResourceJson.title;
            newResourceDetail.revisedResourceJson.title = metadataState.title;
            newResourceDetail.displayTitle = metadataState.title;
            if (metadataState.title !== undefined &&
                JSON.stringify(previousFinalValue) !== JSON.stringify(metadataState.title)) {
                console.log("resourceUpdate for Composition.title");
                dispatch(resourceUpdated(newResourceDetail));
                updateChangeReport(dispatch, resourceChanges, newResourceDetail.id,
                    "string",
                    "title",
                    previousInitialValue,
                    metadataState.title);
            }
        }
    }, [metadataState.title]);

    useEffect(() => {
        if (editMode && metadataState.loaded) {
            let newResourceDetail = JSON.parse(JSON.stringify(parentResourceDetail));
            let previousInitialValue = null;
            let previousFinalValue = null;
            previousInitialValue = initialResourceJson.identifier;
            previousFinalValue = newResourceDetail.revisedResourceJson.identifier;
            newResourceDetail.revisedResourceJson.identifier = metadataState.identifier;
            if (metadataState.identifier !== undefined &&
                JSON.stringify(previousFinalValue) !== JSON.stringify(metadataState.identifier)) {
                console.log("resourceUpdate for Composition.identifier");
                dispatch(resourceUpdated(newResourceDetail));
                updateChangeReport(dispatch, resourceChanges, newResourceDetail.id,
                    "ArrayOfIdentifier",
                    "identifier",
                    previousInitialValue,
                    metadataState.identifier);
            }
        }
    }, [metadataState.identifier]);

    useEffect(() => {
        if (editMode && metadataState.loaded) {
            let newResourceDetail = JSON.parse(JSON.stringify(parentResourceDetail));
            let previousInitialValue = null;
            let previousFinalValue = null;
            previousInitialValue = initialResourceJson.meta.profile;
            previousFinalValue = newResourceDetail.revisedResourceJson.meta.profile;
            newResourceDetail.revisedResourceJson.meta.profile = metadataState.metaProfile;
            if (metadataState.metaProfile !== undefined &&
                JSON.stringify(previousFinalValue) !== JSON.stringify(metadataState.metaProfile)) {
                console.log("resourceUpdate for Composition.meta.profile");
                dispatch(resourceUpdated(newResourceDetail));
                updateChangeReport(dispatch, resourceChanges, newResourceDetail.id,
                    "ArrayOfProfile",
                    "meta.profile",
                    previousInitialValue,
                    metadataState.metaProfile);
            }
        }
    }, [metadataState.metaProfile]);

    useEffect(() => {
        if (editMode && metadataState.loaded) {
            let newResourceDetail = JSON.parse(JSON.stringify(parentResourceDetail));
            let previousInitialValue = null;
            let previousFinalValue = null;
            previousInitialValue = initialResourceJson.language;
            previousFinalValue = newResourceDetail.revisedResourceJson.language;
            newResourceDetail.revisedResourceJson.language = metadataState.language;
            if (metadataState.language !== undefined &&
                JSON.stringify(previousFinalValue) !== JSON.stringify(metadataState.language)
                && !(!previousInitialValue && !metadataState.language)) {
                console.log("resourceUpdate for Composition.language");
                dispatch(resourceUpdated(newResourceDetail));
                updateChangeReport(dispatch, resourceChanges, newResourceDetail.id,
                    "string",
                    "language",
                    previousInitialValue,
                    metadataState.language);
            }
        }
    }, [metadataState.language]);

    useEffect(() => {
        if (editMode && metadataState.loaded) {
            let newResourceDetail = JSON.parse(JSON.stringify(parentResourceDetail));
            let previousInitialValue = null;
            let previousFinalValue = null;
            previousInitialValue = initialResourceJson.version;
            previousFinalValue = newResourceDetail.revisedResourceJson.version;
            newResourceDetail.revisedResourceJson.version = metadataState.version;
            if (metadataState.version !== undefined &&
                JSON.stringify(previousFinalValue) !== JSON.stringify(metadataState.version)
                && !(!previousInitialValue && !metadataState.version)) {
                console.log("resourceUpdate for Composition.version");
                dispatch(resourceUpdated(newResourceDetail));
                updateChangeReport(dispatch, resourceChanges, newResourceDetail.id,
                    "string",
                    "version",
                    previousInitialValue,
                    metadataState.version);
            }
        }
    }, [metadataState.version]);

    useEffect(() => {
        if (editMode && metadataState.loaded) {
            let newResourceDetail = JSON.parse(JSON.stringify(parentResourceDetail));
            let previousInitialValue = null;
            let previousFinalValue = null;
            previousInitialValue = initialResourceJson.status;
            previousFinalValue = newResourceDetail.revisedResourceJson.status;
            newResourceDetail.revisedResourceJson.status = metadataState.status;
            if (metadataState.status !== undefined &&
                JSON.stringify(previousFinalValue) !== JSON.stringify(metadataState.status)
                && !(!previousInitialValue && !metadataState.status)) {
                console.log("resourceUpdate for Composition.status");
                dispatch(resourceUpdated(newResourceDetail));
                updateChangeReport(dispatch, resourceChanges, newResourceDetail.id,
                    "string",
                    "status",
                    previousInitialValue,
                    metadataState.status);
            }
        }
    }, [metadataState.status]);

    useEffect(() => {
        if (editMode && metadataState.loaded) {
            let newResourceDetail = JSON.parse(JSON.stringify(parentResourceDetail));
            let previousInitialValue = null;
            let previousFinalValue = null;
            previousInitialValue = initialResourceJson.date;
            previousFinalValue = newResourceDetail.revisedResourceJson.date;
            newResourceDetail.revisedResourceJson.date = metadataState.date;
            if (metadataState.date !== undefined &&
                JSON.stringify(previousFinalValue) !== JSON.stringify(metadataState.date)
                && !(!previousInitialValue && !metadataState.date)) {
                console.log("resourceUpdate for Composition.date");
                dispatch(resourceUpdated(newResourceDetail));
                updateChangeReport(dispatch, resourceChanges, newResourceDetail.id,
                    "string",
                    "date",
                    previousInitialValue,
                    metadataState.date);
            }
        }
    }, [metadataState.date]);

    useEffect(() => {
        if (editMode && metadataState.loaded) {
            let newResourceDetail = JSON.parse(JSON.stringify(parentResourceDetail));
            let previousInitialValue = null;
            let previousFinalValue = null;
            previousInitialValue = initialResourceJson.custodian;
            previousFinalValue = newResourceDetail.revisedResourceJson.custodian;
            newResourceDetail.revisedResourceJson.custodian = metadataState.custodian;
            if (metadataState.custodian !== undefined &&
                JSON.stringify(previousFinalValue) !== JSON.stringify(metadataState.custodian)
                && !(!previousInitialValue && !metadataState.custodian)) {
                console.log("resourceUpdate for Composition.custodian");
                dispatch(resourceUpdated(newResourceDetail));
                updateChangeReport(dispatch, resourceChanges, newResourceDetail.id,
                    "Reference",
                    "custodian",
                    previousInitialValue,
                    metadataState.custodian);
            }
        }
    }, [metadataState.custodian]);

    useEffect(() => {
        if (editMode && metadataState.loaded) {
            let newResourceDetail = JSON.parse(JSON.stringify(parentResourceDetail));
            let previousInitialValue = null;
            let previousFinalValue = null;
            previousInitialValue = initialResourceJson.useContext;
            previousFinalValue = newResourceDetail.revisedResourceJson.useContext;
            newResourceDetail.revisedResourceJson.useContext = metadataState.useContext;
            if (metadataState.useContext !== undefined &&
                JSON.stringify(previousFinalValue) !== JSON.stringify(metadataState.useContext)
                && !(previousInitialValue === undefined &&
                    JSON.stringify(metadataState.useContext) === "[]")) {
                console.log("resourceUpdate for Composition.useContext");
                dispatch(resourceUpdated(newResourceDetail));
                updateChangeReport(dispatch, resourceChanges, newResourceDetail.id,
                    "ArrayOfUsageContext",
                    "useContext",
                    previousInitialValue,
                    metadataState.useContext);
            }
        }
    }, [metadataState.useContext]);

    useEffect(() => {
        if (editMode && metadataState.loaded) {
            let newResourceDetail = JSON.parse(JSON.stringify(parentResourceDetail));
            let previousInitialValue = null;
            let previousFinalValue = null;
            previousInitialValue = initialResourceJson.note;
            previousFinalValue = newResourceDetail.revisedResourceJson.note;
            newResourceDetail.revisedResourceJson.note = metadataState.note;
            if (metadataState.note !== undefined &&
                JSON.stringify(previousFinalValue) !== JSON.stringify(metadataState.note)
                && !(previousInitialValue === undefined &&
                    JSON.stringify(metadataState.note) === "[]")) {
                console.log("resourceUpdate for Composition.note");
                dispatch(resourceUpdated(newResourceDetail));
                updateChangeReport(dispatch, resourceChanges, newResourceDetail.id,
                    "ArrayOfAnnotation",
                    "note",
                    previousInitialValue,
                    metadataState.note);
            }
        }
    }, [metadataState.note]);

    useEffect(() => {
        if (editMode && metadataState.loaded) {
            let newResourceDetail = JSON.parse(JSON.stringify(parentResourceDetail));
            let previousInitialValue = null;
            let previousFinalValue = null;
            previousInitialValue = initialResourceJson.author;
            previousFinalValue = newResourceDetail.revisedResourceJson.author;
            newResourceDetail.revisedResourceJson.author = metadataState.author;
            if (metadataState.author !== undefined &&
                JSON.stringify(previousFinalValue) !== JSON.stringify(metadataState.author)
                && !(previousInitialValue === undefined &&
                    JSON.stringify(metadataState.author) === "[]")) {
                console.log("resourceUpdate for Composition.author");
                dispatch(resourceUpdated(newResourceDetail));
                updateChangeReport(dispatch, resourceChanges, newResourceDetail.id,
                    "ArrayOfReference",
                    "author",
                    previousInitialValue,
                    metadataState.author);
            }
        }
    }, [metadataState.author]);

    useEffect(() => {
        if (editMode && metadataState.loaded) {
            let newResourceDetail = JSON.parse(JSON.stringify(parentResourceDetail));
            let previousInitialValue = null;
            let previousFinalValue = null;
            previousInitialValue = initialResourceJson.attester;
            previousFinalValue = newResourceDetail.revisedResourceJson.attester;
            newResourceDetail.revisedResourceJson.attester = metadataState.attester;
            if (metadataState.attester !== undefined &&
                JSON.stringify(previousFinalValue) !== JSON.stringify(metadataState.attester)
                && !(previousInitialValue === undefined &&
                    JSON.stringify(metadataState.attester) === "[]")) {
                console.log("resourceUpdate for Composition.attester");
                dispatch(resourceUpdated(newResourceDetail));
                updateChangeReport(dispatch, resourceChanges, newResourceDetail.id,
                    "ArrayOfAttester",
                    "attester",
                    previousInitialValue,
                    metadataState.attester);
            }
        }
    }, [metadataState.attester]);

    useEffect(() => {
        if (editMode && metadataState.loaded) {
            let newResourceDetail = JSON.parse(JSON.stringify(parentResourceDetail));
            let previousInitialValue = null;
            let previousFinalValue = null;
            previousInitialValue = initialResourceJson.relatesTo;
            previousFinalValue = newResourceDetail.revisedResourceJson.relatesTo;
            newResourceDetail.revisedResourceJson.relatesTo = metadataState.relatesTo;
            if (metadataState.relatesTo !== undefined &&
                JSON.stringify(previousFinalValue) !== JSON.stringify(metadataState.relatesTo)
                && !(previousInitialValue === undefined &&
                    JSON.stringify(metadataState.relatesTo) === "[]")) {
                console.log("resourceUpdate for Composition.relatesTo");
                dispatch(resourceUpdated(newResourceDetail));
                updateChangeReport(dispatch, resourceChanges, newResourceDetail.id,
                    "ArrayOfRelatesTo",
                    "relatesTo",
                    previousInitialValue,
                    metadataState.relatesTo);
            }
        }
    }, [metadataState.relatesTo]);

    const statusSetterFunction = (value) => {
        setMetadataState(prevState => { return { ...prevState, "status": value }; });
    }

    return <div id="metadata">
        <h2>Metadata</h2>
        {editMode ?
            <div style={{ marginLeft: "24px" }}>
                <StringEntry elementName='title' fieldLabel='Title'
                    startingValue={metadataState.title} setResourceState={setMetadataState} />
                <ArrayEntry datatype="Identifier" elementName="identifier"
                    fieldLabel="Identifier" preserveFOI={true}
                    startingValue={metadataState.identifier}
                    setResourceState={setMetadataState}
                    deletable deletableArray doNotShowFieldLabel={false}
                    startEmptyArrayClosed startCollapsed />
                <ArrayEntry datatype="Profile" elementName="metaProfile"
                    fieldLabel="Profile" resourceType="Composition"
                    startingValue={metadataState.metaProfile}
                    setResourceState={setMetadataState}
                    deletable deletableArray doNotShowFieldLabel={true}
                    startEmptyArrayClosed startCollapsed />
                <CodeEntry elementName='language' fieldLabel='Language'
                    startingValue={metadataState.language} setResourceState={setMetadataState}
                    allowedValues={languageCodes} />
                {extensionState && <ArrayEntry datatype="code" elementName='additionalLanguage'
                    fieldLabel='Additional Language' allowedValues={languageCodes}
                    startingValue={extensionState.additionalLanguage} setResourceState={setExtensionState} />}
                <StringEntry elementName='version' fieldLabel='Version'
                    startingValue={metadataState.version} setResourceState={setMetadataState} />
                {extensionState && <StringEntry elementName='versionAlgorithmString'
                    fieldLabel='Version Algorithm'
                    startingValue={extensionState.versionAlgorithmString} setResourceState={setExtensionState} />}
                <b>Status: </b>
                <ToggleButtonGroup
                    className={"genericToggleButtonGroup buttonGroupWrap"}
                    exclusive
                    value={metadataState.status || ""}
                    aria-label={'Status'}
                    name={'Status'} >
                    <ToggleButton key={0} className="genericToggleButton" color="primary"
                        aria-label={'Registered'} value={'registered'}
                        onClick={() => { statusSetterFunction('registered'); }}>
                        Registered
                    </ToggleButton>
                    <ToggleButton key={1} className="genericToggleButton" color="primary"
                        aria-label={'Draft'} value={'partial'}
                        onClick={() => { statusSetterFunction('partial'); }}>
                        Draft
                    </ToggleButton>
                    <ToggleButton key={2} className="genericToggleButton" color="primary"
                        aria-label={'Published'} value={'final'}
                        onClick={() => { statusSetterFunction('final'); }}>
                        Published (public)
                    </ToggleButton>
                    <ToggleButton key={3} className="genericToggleButton" color="primary"
                        aria-label={'Amended'} value={'amended'}
                        onClick={() => { statusSetterFunction('amended'); }}>
                        Amended (public)
                    </ToggleButton>
                    <ToggleButton key={4} className="genericToggleButton" color="primary"
                        aria-label={'Retired'} value={'deprecated'}
                        onClick={() => { statusSetterFunction('deprecated'); }}>
                        Retired
                    </ToggleButton>
                    <ToggleButton key={5} className="genericToggleButton" color="primary"
                        aria-label={'Unknown'} value={'unknown'}
                        onClick={() => { statusSetterFunction('unknown'); }}>
                        Unknown (public)
                    </ToggleButton>
                </ToggleButtonGroup>
                <DateTimeEntry elementName='date' fieldLabel='Date last changed'
                    startingValue={metadataState.date} setResourceState={setMetadataState} />
                <ReferenceEntry elementName='custodian' fieldLabel='Custodian/Publisher'
                    startingValue={metadataState.custodian} setResourceState={setMetadataState}
                    referencedResourceTypes={['Organization']} startCollapsed={true} />
                {extensionState && <ArrayEntry datatype="ExtendedContactDetail" elementName='extendedContactDetail'
                    fieldLabel='Publisher Contact'
                    startingValue={extensionState.extendedContactDetail} setResourceState={setExtensionState} />}
                {extensionState && <MarkdownEntry elementName='description'
                    fieldLabel='Description'
                    startingValue={extensionState.description} setResourceState={setExtensionState} />}
                <ArrayEntry datatype="UsageContext" elementName="useContext"
                    fieldLabel="Use Context"
                    startingValue={metadataState.useContext}
                    setResourceState={setMetadataState}
                    deletable deletableArray
                    startEmptyArrayClosed startCollapsed />
                <ArrayEntry datatype="Annotation" elementName="note"
                    fieldLabel="Note"
                    startingValue={metadataState.note}
                    setResourceState={setMetadataState}
                    deletable deletableArray
                    startEmptyArrayClosed startCollapsed />
                {extensionState && <MarkdownEntry elementName='copyright'
                    fieldLabel='Copyright'
                    startingValue={extensionState.copyright} setResourceState={setExtensionState} />}
                {extensionState && <StringEntry elementName='copyrightLabel'
                    fieldLabel='Copyright label'
                    startingValue={extensionState.copyrightLabel} setResourceState={setExtensionState} />}
                {extensionState && <DateEntry elementName='approvalDate'
                    fieldLabel='Date approved'
                    startingValue={extensionState.approvalDate} setResourceState={setExtensionState} />}
                {extensionState && <DateEntry elementName='lastReviewDate'
                    fieldLabel='Date last reviewed'
                    startingValue={extensionState.lastReviewDate} setResourceState={setExtensionState} />}
                {extensionState && <DateEntry elementName='publicationDate'
                    fieldLabel='Date published'
                    startingValue={extensionState.publicationDate} setResourceState={setExtensionState} />}
                {extensionState && <PeriodEntry elementName='effectivePeriod'
                    fieldLabel='Effective period'
                    startingValue={extensionState.effectivePeriod} setResourceState={setExtensionState} />}
                <ArrayEntry datatype="Reference" elementName="author"
                    fieldLabel="Author" dataEntryStyle={'humanAuthor'}
                    referencedResourceTypes={['Practitioner', 'Organization']}
                    startingValue={metadataState.author}
                    setResourceState={setMetadataState}
                    deletable deletableArray
                    startEmptyArrayClosed startCollapsed />
                <ArrayEntry datatype="Attester" elementName="attester"
                    fieldLabel="Attester"
                    startingValue={metadataState.attester}
                    setResourceState={setMetadataState}
                    deletable deletableArray
                    startEmptyArrayClosed startCollapsed />
                <ArrayEntry datatype="RelatesTo" elementName="relatesTo"
                    fieldLabel="Related Item"
                    startingValue={metadataState.relatesTo}
                    setResourceState={setMetadataState}
                    deletable deletableArray
                    startEmptyArrayClosed startCollapsed />
            </div>
            :
            <div style={{ marginLeft: "24px" }}>
                <p><b>Title: </b>{fhirJson.title}</p>
                <p><b>URL: </b>{fhirJson.url} or https://computablepublishing.com/Composition/{fhirJson.id}</p>
                {fhirJson.identifier.map((identifier, identifierIndex) => {
                    return <p key={identifierIndex}><b>Identifier: </b>{displayStringFromFHIRIdentifier(identifier)}</p>
                })}
                {fhirJson.meta.profile.map((profile, profileIndex) => {
                    return <p key={profileIndex}><b>Profile: </b>{displayProfileName(profile, fhirJson.resourceType)}</p>
                })}
                {fhirJson.language && <p><b>Language: </b>{languageCodeDisplay[fhirJson.language]}</p>}
                {(additionalLanguages.length > 0) && additionalLanguages.map(additionalLanguage => {
                    return <p><b>Additional Language: </b>{languageCodeDisplay[additionalLanguage]}</p>
                })}
                {fhirJson.version && <p><b>Version: </b>{fhirJson.version}</p>}
                {versionAlgorithmString && <p><b>Version Algorithm: </b>{versionAlgorithmString}</p>}
                {fhirJson.status && <p><b>Status: </b>{fhirJson.status}</p>}
                {fhirJson.date && <p><b>Date last changed: </b>{getDateTimeDisplay(fhirJson.date)}</p>}
                {fhirJson.custodian && <p><b>Custodian/Publisher: </b><DisplayFromFHIRReference reference={fhirJson.custodian} /></p>}
                {(extendedContactDetail.length > 0) && extendedContactDetail.map(extendedContactDetail => {
                    return <p><b>Publisher Contact: </b>{DisplayFromFHIRExtendedContactDetail[extendedContactDetail]}</p>
                })}
                {description && <div><b>Description: </b><DisplayFromFHIRMarkdown markdown={description} /><br /><br /></div>}
                {(Array.isArray(fhirJson.useContext) && fhirJson.useContext.length > 0) &&
                    fhirJson.useContext.map((useContextInstance, useContextIndex) => {
                        return <div key={useContextIndex}>
                            <div><b>Use Context: </b><div style={{ marginLeft: "24px" }}>
                                <DisplayFromFHIRUsageContext usageContext={useContextInstance} />
                            </div></div><br />
                        </div>
                    })}
                {(Array.isArray(fhirJson.note) && fhirJson.note.length > 0) &&
                    fhirJson.note.map((noteInstance, noteIndex) => {
                        return <p key={noteIndex}>
                            <b>Use Context: </b>{displayStringFromFHIRAnnotation(noteInstance)}
                        </p>
                    })}
                {copyright && <div><b>Copyright: </b><DisplayFromFHIRMarkdown markdown={copyright} /><br /><br /></div>}
                {copyrightLabel && <p><b>Copyright: </b>{copyrightLabel}</p>}
                {approvalDate && <p><b>Date approved: </b>{getDateDisplay(approvalDate)}</p>}
                {lastReviewDate && <p><b>Date last reviewed: </b>{getDateDisplay(lastReviewDate)}</p>}
                {publicationDate && <p><b>Date published: </b>{getDateDisplay(publicationDate)}</p>}
                {effectivePeriod && <p><b>Effective period: </b>{displayStringFromFHIRPeriod(effectivePeriod)}</p>}
                {(Array.isArray(fhirJson.author) && fhirJson.author.length === 1) &&
                    <div><b>Author: </b>
                        <>{fhirJson.author[0].name ?
                            <div style={{ marginLeft: "24px" }}><DisplayFromFHIRContactDetail contactDetail={fhirJson.author[0]} /></div>
                            :
                            <div style={{ marginLeft: "24px" }}><DisplayFromFHIRReference reference={fhirJson.author[0]} /></div>
                        }</>
                        <br />
                    </div>
                }
                {(Array.isArray(fhirJson.author) && fhirJson.author.length > 1) && fhirJson.author.map((contact, contactIndex) => {
                    return <div key={contactIndex}><div><b>Author: </b><div style={{ marginLeft: "24px" }}><DisplayFromFHIRReference reference={contact} /></div></div></div>
                })}
                {(Array.isArray(fhirJson.attester) && fhirJson.attester.length === 1) &&
                    <div><b>Attester: </b>
                        <div style={{ marginLeft: "24px" }}>
                            <DisplayFromFHIRAttester attester={fhirJson.attester[0]} />
                        </div><br />
                    </div>}
                {(Array.isArray(fhirJson.attester) && fhirJson.attester.length > 1) && fhirJson.attester.map((attester, attesterIndex) => {
                    return <div key={attesterIndex}>
                        <div><b>Attester: </b>
                            <div style={{ marginLeft: "24px" }}>
                                <DisplayFromFHIRAttester attester={attester} />
                            </div>
                        </div>
                    </div>
                })}
                {(Array.isArray(fhirJson.relatesTo) && fhirJson.relatesTo.length > 0) &&
                    fhirJson.relatesTo.map((relatedItem, relatedItemIndex) => {
                        return <div key={relatedItemIndex}>
                            <div><b>Related Item: </b>
                                <div style={{ marginLeft: "24px" }}><DisplayFromFHIRRelatesTo relatesTo={relatedItem} /></div>
                            </div>
                        </div>
                    })}
            </div>}
    </div>
}

const createEvidenceDisplay = (item) => {
    let description = item.description;
    let statistics = item.statistic?.map(statistic => {
        let statisticDisplay = "";
        if (statistic.category) {
            let categoryDisplay = statistic.category.text || statistic.category.coding?.[0]?.display;
            statisticDisplay = "for " + categoryDisplay + ", "
        }
        if (statistic.sampleSize?.numberAnalyzed || statistic.sampleSize?.numberAnalyzed === 0) {
            statisticDisplay += "out of " + statistic.sampleSize.numberAnalyzed + ": ";
        } else if (statistic.sampleSize?.knownDataCount || statistic.sampleSize?.knownDataCount === 0) {
            statisticDisplay += "out of " + statistic.sampleSize.knownDataCount + ": ";
        } else if (statistic.sampleSize?.numberOfParticipants || statistic.sampleSize?.numberOfParticipants === 0) {
            statisticDisplay += "out of " + statistic.sampleSize.numberOfParticipants + ": ";
        }
        if (statistic.description) {
            statisticDisplay += statistic.description;
        } else {
            let statisticType = statistic.statisticType?.coding?.[0]?.display || statistic.statisticType?.text || "";
            let statisticQuantity = displayStringFromFHIRQuantity(statistic.quantity) || "";
            statisticDisplay += statisticType + " " + statisticQuantity;
            if (statistic.attributeEstimate?.length) {
                let attributeEstimatesDisplay = statistic.attributeEstimate.map(estimate => {
                    let estimateDisplay = estimate.description || "";
                    if (!estimateDisplay) {
                        let estimateType = estimate.type?.coding?.[0]?.display || estimate.type?.text || "";
                        let estimateQuantity = displayStringFromFHIRQuantity(estimate.quantity) || "";
                        let estimateRange = displayStringFromFHIRRange(estimate.range) || "";
                        if (estimate.level) {
                            estimateDisplay = (100 * estimate.level).toString() + "% ";
                        }
                        estimateDisplay += estimateType + " " + estimateQuantity + estimateRange;
                    }
                    if (estimate.note?.length) {
                        for (let note of estimate.note) {
                            if (note.text) {
                                estimateDisplay += "; " + note.text;
                            }
                        }
                    }
                    return estimateDisplay;
                }).join(", ");
                statisticDisplay += " (" + attributeEstimatesDisplay + ")";
            }
        }
        if (statistic.note?.length) {
            for (let note of statistic.note) {
                if (note.text) {
                    statisticDisplay += "; " + note.text;
                }
            }
        }
        return statisticDisplay;
    }).join("; ");
    let notes = item.note?.map(note => note.text).join("; ");
    let evidenceDisplay = "";
    if (statistics) {
        evidenceDisplay = statistics;
    } else if (description) {
        evidenceDisplay = description;
    }
    if (notes) {
        if (evidenceDisplay) {
            evidenceDisplay += "; ";
        }
        evidenceDisplay += notes;
    }
    return evidenceDisplay;
}

const addToEvidenceDisplayDictionary = (item, evidenceDisplayDictionary) => {
    let evidenceDisplay = createEvidenceDisplay(item);
    evidenceDisplayDictionary[item.id] = evidenceDisplay;
}

const CompositionDisplay = ({ foi }) => {
    const parentResourceDetail = useSelector(state => selectResourceById(state, foi));
    const allResources = useSelector(state => selectAllResources(state));

    const editMode = useSelector(state => selectEditMode(state));
    const unsavedChanges = useSelector(state => selectChangedResourcesListStatus(state));
    const changedResourcesList = useSelector(state => selectChangedResourcesList(state));
    const resourceCollection = useSelector(state => selectAllResources(state));
    const apiToken = useSelector(selectCurrentUserApiToken);
    const dispatch = useDispatch();

    const editPermission = parentResourceDetail?.usageData?.editpermission;
    const [editSectionModal, setEditSectionModal] = useState({ "openModal": false });

    const [compositionState, setCompositionState] = useState({});

    const resourceChanges = useSelector(state => selectAllResourceChanges(state));
    const [resourceLoaded, setResourceLoaded] = useState({});
    const [sofFoiState, setSofFoiState] = useState("");
    const userName = useSelector(state => selectCurrentUserName(state));

    useEffect(() => {
        if (parentResourceDetail?.revisedResourceJson) {
            let fhirJson = parentResourceDetail.revisedResourceJson;
            let generatedNarrative;
            if (fhirJson.type?.text === "Summary of Findings") {
                generatedNarrative = generateSummaryOfFindingsNarrative(fhirJson);
            } else if (fhirJson.type?.text === "Outcome Measure Report") {
                generatedNarrative = generatedNarrativeFromEvidenceTable(fhirJson.section);
            }
            let resourceDictionary = {};
            let containedDictionary = {};
            let combinedDictionary = {};
            let groupReferencesDictionary = {};
            let groupReferencesDefinedInGroupList = false;
            let evidenceDisplayDictionary = {};
            let type = fhirJson.type?.text;
            const measureReport = type === "Outcome Measure Report" || type === "Baseline Measure Report" ||
                type === "Participant Flow Report";

            let resultsSectionsKeys = [];
            let resultsSectionsDictionary = {};
            let resourceReferenceMap = {};
            if (measureReport && Array.isArray(fhirJson.section) && fhirJson.section.length > 0) {
                for (const sectionIndex in fhirJson.section) {
                    const section = fhirJson.section[sectionIndex];
                    const sectionPath = [sectionIndex];
                    const sectionPathString = sectionPath.join(",");
                    if (section?.code?.coding?.[0]?.code === "results") {
                        resultsSectionsKeys.push(sectionPathString);
                        resultsSectionsDictionary[sectionPathString] = section;
                    } else if (section?.code?.text === "OutcomeGroupList" || section?.code?.text === "BaselineGroupList" ||
                        section?.code?.text === "FlowGroupList" || section?.code?.text === "GroupList") {
                        if (section.entry?.length) {
                            for (let entry of section.entry) {
                                let entryId = getIdFromReference(entry);
                                if (!groupReferencesDictionary[entryId]) {
                                    groupReferencesDictionary[entryId] = entry;
                                }
                            }
                        }
                    } else {
                        if (section?.section?.length > 0) {
                            let sectionFocus = section.focus;
                            for (const subsectionIndex in section.section) {
                                const subsection = section.section[subsectionIndex];
                                const subsectionPath = sectionPath.concat([subsectionIndex]);
                                const subsectionPathString = subsectionPath.join(",");
                                if (subsection?.code?.coding?.[0]?.code === "results") {
                                    if (!subsection.focus && sectionFocus) {
                                        subsection.focus = sectionFocus;
                                    }
                                    resultsSectionsKeys.push(subsectionPathString);
                                    resultsSectionsDictionary[subsectionPathString] = subsection;
                                } else if (subsection?.code?.text === "OutcomeGroupList" || subsection?.code?.text === "BaselineGroupList" ||
                                    subsection?.code?.text === "FlowGroupList" || section?.code?.text === "GroupList") {
                                    if (subsection.entry?.length) {
                                        for (let entry of subsection.entry) {
                                            let entryId = getIdFromReference(entry);
                                            if (!groupReferencesDictionary[entryId]) {
                                                groupReferencesDictionary[entryId] = entry;
                                            }
                                        }
                                    }
                                } else {
                                    if (subsection.focus) {
                                        sectionFocus = subsection.focus;
                                    }
                                    if (subsection?.section?.length > 0) {
                                        for (const subsubsectionIndex in subsection.section) {
                                            const subsubsection = subsection.section[subsubsectionIndex];
                                            const subsubsectionPath = subsectionPath.concat([subsubsectionIndex]);
                                            const subsubsectionPathString = subsubsectionPath.join(",");
                                            if (subsubsection?.code?.coding?.[0]?.code === "results") {
                                                if (!subsubsection.focus && sectionFocus) {
                                                    subsubsection.focus = sectionFocus;
                                                }
                                                resultsSectionsKeys.push(subsubsectionPathString);
                                                resultsSectionsDictionary[subsubsectionPathString] = subsubsection;
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
                if (Object.keys(groupReferencesDictionary).length > 0) {
                    groupReferencesDefinedInGroupList = true;
                }
            }
            if (type === 'Comparative Evidence Report') {
                for (const sectionIndex in fhirJson.section) {
                    const section = fhirJson.section[sectionIndex];
                    if (section?.code?.coding?.[0]?.code === "population") {
                        if (section.entry?.[0]) {
                            resourceReferenceMap.populationReference = section.entry[0];
                        }
                        if (section.section?.length) {
                            for (const subsectionIndex in section.section) {
                                const subsection = section.section[subsectionIndex];
                                if (subsection?.code?.coding?.[0]?.code === "screened-group" &&
                                    subsection.entry?.[0]) {
                                    resourceReferenceMap.screenedGroupReference = subsection.entry[0];
                                } else if (subsection?.code?.coding?.[0]?.code === "enrolled-group" &&
                                    subsection.entry?.[0]) {
                                    resourceReferenceMap.enrolledGroupReference = subsection.entry[0];
                                } else if (subsection?.code?.coding?.[0]?.code === "analyzed-group" &&
                                    subsection.entry?.[0]) {
                                    resourceReferenceMap.analyzedGroupReference = subsection.entry[0];
                                } else if (subsection?.code?.coding?.[0]?.code === "population-description" &&
                                    subsection.entry?.[0]) {
                                    resourceReferenceMap.populationDescriptionGroupReference = subsection.entry[0];
                                }
                            }
                        }
                    } else if (section?.code?.coding?.[0]?.code === "intervention") {
                        if (section.section?.length) {
                            for (const subsectionIndex in section.section) {
                                const subsection = section.section[subsectionIndex];
                                if (subsection?.code?.coding?.[0]?.code === "intervention-description" &&
                                    subsection.entry?.[0]) {
                                    resourceReferenceMap.interventionDescriptionReference = subsection.entry[0];
                                } else if (subsection?.code?.coding?.[0]?.code === "intervention-group" &&
                                    subsection.entry?.[0]) {
                                    resourceReferenceMap.interventionGroupReference = subsection.entry[0];
                                }
                            }
                        }
                    } else if (section?.code?.coding?.[0]?.code === "comparator") {
                        if (section.section?.length) {
                            for (const subsectionIndex in section.section) {
                                const subsection = section.section[subsectionIndex];
                                if (subsection?.code?.coding?.[0]?.code === "comparator-description" &&
                                    subsection.entry?.[0]) {
                                    resourceReferenceMap.comparatorDescriptionReference = subsection.entry[0];
                                } else if (subsection?.code?.coding?.[0]?.code === "comparator-group" &&
                                    subsection.entry?.[0]) {
                                    resourceReferenceMap.comparatorGroupReference = subsection.entry[0];
                                } else if (subsection?.code?.coding?.[0]?.code === "GroupAssignment" &&
                                    subsection.entry?.[0]) {
                                    resourceReferenceMap.groupAssignmentReference = subsection.entry[0];
                                }
                            }
                        }
                    } else if (section?.code?.coding?.[0]?.code === "research-study") {
                        if (section.entry?.[0]) {
                            resourceReferenceMap.researchStudyReference = section.entry[0];
                        }
                    } else if (section?.code?.coding?.[0]?.code === "baseline-measures") {
                        if (section.entry?.length) {
                            for (const entry of section.entry) {
                                if (entry.type === "List") {
                                    resourceReferenceMap.baselineVariablesListReference = entry;
                                } else if (entry.type === "Composition") {
                                    resourceReferenceMap.baselineMeasuresReportReference = entry;
                                }
                            }
                        }
                    } else if (section?.code?.coding?.[0]?.code === "participant-flow") {
                        if (section.entry?.length) {
                            for (const entry of section.entry) {
                                if (entry.type === "List") {
                                    resourceReferenceMap.participantFlowVariablesListReference = entry;
                                } else if (entry.type === "Composition") {
                                    resourceReferenceMap.participantFlowReportReference = entry;
                                }
                            }
                        }
                    } else if (section?.code?.coding?.[0]?.code === "outcome-measures") {
                        if (section.entry?.length) {
                            for (const entry of section.entry) {
                                if (entry.type === "List") {
                                    resourceReferenceMap.outcomeVariablesListReference = entry;
                                } else if (entry.type === "Composition") {
                                    resourceReferenceMap.outcomeMeasuresReportReference = entry;
                                }
                            }
                        }
                    } else if (section?.code?.coding?.[0]?.code === "summary-of-findings") {
                        if (section.entry?.length) {
                            for (const entry of section.entry) {
                                if (entry.type === "List") {
                                    resourceReferenceMap.outcomeVariablesListReference = entry;
                                } else if (entry.type === "Composition") {
                                    resourceReferenceMap.summaryOfFindingsReference = entry;
                                }
                            }
                        }
                    }
                }
            }
            if (type === 'Comparative Evidence Synthesis Report') {
                for (const sectionIndex in fhirJson.section) {
                    const section = fhirJson.section[sectionIndex];
                    if (section?.code?.coding?.[0]?.code === "population") {
                        if (section.entry?.[0]) {
                            resourceReferenceMap.synthesisPopulationReference = section.entry[0];
                        }
                    } else if (section?.code?.coding?.[0]?.code === "intervention-description") {
                        if (section.entry?.[0]) {
                            resourceReferenceMap.synthesisInterventionDescriptionReference = section.entry[0];
                        }
                    } else if (section?.code?.coding?.[0]?.code === "comparator-description") {
                        if (section.entry?.[0]) {
                            resourceReferenceMap.synthesisComparatorDescriptionReference = section.entry[0];
                        }
                    } else if (section?.code?.coding?.[0]?.code === "research-study") {
                        if (section.entry?.[0]) {
                            resourceReferenceMap.researchStudyReference = section.entry[0];
                        }
                    } else if (section?.code?.coding?.[0]?.code === "variables") {
                        if (section.entry?.length) {
                            for (const entry of section.entry) {
                                if (entry.type === "List") {
                                    resourceReferenceMap.outcomeVariablesListReference = entry;
                                } else if (entry.type === "EvidenceVariable") {
                                    if (!resourceReferenceMap.variablesReferenceList) {
                                        resourceReferenceMap.variablesReferenceList = [];
                                    }
                                    resourceReferenceMap.variablesReferenceList.push(entry);
                                }
                            }
                        }
                    } else if (section?.code?.coding?.[0]?.code === "results") {
                        if (section.entry?.length) {
                            for (const entry of section.entry) {
                                if (entry.type === "Composition") {
                                    resourceReferenceMap.outcomeMeasuresReportReference = entry;
                                }
                            }
                        }
                    } else if (section?.code?.coding?.[0]?.code === "summary-of-findings") {
                        if (section.entry?.length) {
                            for (const entry of section.entry) {
                                if (entry.type === "Composition") {
                                    resourceReferenceMap.summaryOfFindingsReference = entry;
                                }
                            }
                        }
                    }
                }
            }
            for (const entry of resourceCollection) {
                resourceDictionary[entry.id] = entry.resourceJson;
                if (measureReport && entry.resourceType === "Evidence") {
                    addToEvidenceDisplayDictionary(entry.resourceJson, evidenceDisplayDictionary);
                    if (!groupReferencesDefinedInGroupList) {
                        if (entry.resourceJson.meta?.profile?.[0] === "http://hl7.org/fhir/uv/ebm/StructureDefinition/comparative-evidence" &&
                            entry.resourceJson.variableDefinition?.[1]?.variableRole === "exposure") {
                            let referenceObject = entry.resourceJson.variableDefinition[1].observed || entry.resourceJson.variableDefinition[1].intended;
                            let entryId = getIdFromReference(referenceObject);
                            if (!groupReferencesDictionary[entryId]) {
                                groupReferencesDictionary[entryId] = referenceObject;
                            }
                        } else if (entry.resourceJson.variableDefinition?.[0]?.variableRole === "population") {
                            let referenceObject = entry.resourceJson.variableDefinition[0].observed || entry.resourceJson.variableDefinition[0].intended;
                            let entryId = getIdFromReference(referenceObject);
                            if (!groupReferencesDictionary[entryId]) {
                                groupReferencesDictionary[entryId] = referenceObject;
                            }
                        }
                    }
                }
                combinedDictionary[entry.id] = entry.resourceJson;
            }
            if (Array.isArray(fhirJson.contained) && fhirJson.contained.length > 0) {
                for (const item of fhirJson.contained) {
                    containedDictionary[item.id] = item;
                    if (measureReport && item.resourceType === "Evidence") {
                        if (!groupReferencesDefinedInGroupList) {
                            addToEvidenceDisplayDictionary(item, evidenceDisplayDictionary);
                            if (item.meta?.profile?.[0] === "http://hl7.org/fhir/uv/ebm/StructureDefinition/comparative-evidence" &&
                                item.variableDefinition?.[1]?.variableRole === "exposure") {
                                let entry = item.variableDefinition[1].observed || item.variableDefinition[1].intended;
                                let entryId = getIdFromReference(entry);
                                if (!groupReferencesDictionary[entryId]) {
                                    groupReferencesDictionary[entryId] = entry;
                                }
                            } else if (item.variableDefinition?.[0]?.variableRole === "population") {
                                let entry = item.variableDefinition[0].observed || item.variableDefinition[0].intended;
                                let entryId = getIdFromReference(entry);
                                if (!groupReferencesDictionary[entryId]) {
                                    groupReferencesDictionary[entryId] = entry;
                                }
                            }
                        }
                    }
                    combinedDictionary[item.id] = item;
                }
            }
            setResourceLoaded({
                "title": fhirJson.title,
                "sections": fhirJson.section,
                "json": fhirJson,
                "generatedNarrative": generatedNarrative,
                "resourceDictionary": resourceDictionary,
                "resultsSectionsKeys": resultsSectionsKeys,
                "resultsSectionsDictionary": resultsSectionsDictionary,
                "containedDictionary": containedDictionary,
                "combinedDictionary": combinedDictionary,
                "groupReferencesDictionary": groupReferencesDictionary,
                "evidenceDisplayDictionary": evidenceDisplayDictionary,
                "resourceReferenceMap": resourceReferenceMap,
                "hasResourceCollection": Object.keys(resourceDictionary).length > 0
            });
            if (fhirJson.relatesTo?.length > 0) {
                for (const item of fhirJson.relatesTo) {
                    if (item.type === "transformed-into") {
                        let resourceFOI = item.targetReference?.reference?.replace("Composition/", "");
                        if (resourceFOI) {
                            setSofFoiState(resourceFOI);
                            break;
                        }
                    }
                }
            }
        }
    }, [parentResourceDetail]);

    useEffect(() => {
        if (resourceLoaded.sections) {
            setCompositionState({
                "section": resourceLoaded.sections,
                "text": resourceLoaded.json.text || emptyTextNoData
            });
        }
    }, [resourceLoaded]);

    useEffect(() => {
        if (editMode && resourceLoaded.sections) {
            let newResourceDetail = JSON.parse(JSON.stringify(parentResourceDetail));
            let previousInitialValue = null;
            let previousFinalValue = null;
            previousInitialValue = newResourceDetail.resourceJson.section;
            previousFinalValue = newResourceDetail.revisedResourceJson.section;
            newResourceDetail.revisedResourceJson.section = compositionState.section;
            if (compositionState.section !== undefined &&
                JSON.stringify(previousFinalValue && previousFinalValue.map(section => section.code)) !== JSON.stringify(compositionState.section.map(section => section.code))
                && !(previousInitialValue === undefined &&
                    JSON.stringify(compositionState.section) === "[]")) {
                console.log("resourceUpdate for Composition.section");
                dispatch(resourceUpdated(newResourceDetail));
                updateChangeReport(dispatch, resourceChanges, newResourceDetail.id,
                    "ArrayOfSection",
                    "section",
                    previousInitialValue,
                    compositionState.section);
            }
        }
    }, [compositionState.section]);

    useEffect(() => {
        if (editMode && resourceLoaded.sections) {
            let newResourceDetail = JSON.parse(JSON.stringify(parentResourceDetail));
            let previousInitialValue = null;
            let previousFinalValue = null;
            previousInitialValue = newResourceDetail.resourceJson.text;
            previousFinalValue = newResourceDetail.revisedResourceJson.text;
            newResourceDetail.revisedResourceJson.text = compositionState.text;
            if (compositionState.text !== undefined &&
                JSON.stringify(previousFinalValue) !== JSON.stringify(compositionState.text) &&
                !(previousFinalValue === undefined && compositionState.text.status === "empty")) {
                console.log("resourceUpdate for Composition.text");
                dispatch(resourceUpdated(newResourceDetail));
                updateChangeReport(dispatch, resourceChanges, newResourceDetail.id,
                    "Narrative",
                    "text",
                    previousInitialValue,
                    compositionState.text);
            }
        }
    }, [compositionState.text]);

    const saveAllChanges = async () => {
        const filteredResourceCollection = resourceCollection.filter(entry => changedResourcesList.includes(entry.id));
        let primaryEntryUpdated;
        for (const entry of filteredResourceCollection) {
            console.log(foi)
            console.log(entry.id)
            let entryFoi = entry.id;
            if (entryFoi === foi) {
                primaryEntryUpdated = entry;
            } else {
                let resourceJson = entry.revisedResourceJson;
                let title = entry.displayTitle;
                console.log(title)
                await updateFhirJson(entryFoi, resourceJson, title, apiToken);
            }
        }
        let resourceJson;
        let title;
        if (primaryEntryUpdated) {
            resourceJson = primaryEntryUpdated.revisedResourceJson;
            title = primaryEntryUpdated.displayTitle;
        } else {
            resourceJson = parentResourceDetail.revisedResourceJson;
            title = parentResourceDetail.displayTitle;
        }
        console.log(title)
        await updateFhirJson(foi, resourceJson, title, apiToken);
        dispatch(changesCleared());
    }

    const createChangeReport = async () => {
        let changeReportJson = {
            "resourceType": "ArtifactAssessment",
            "meta": { "profile": ["https://hl7.org/fhir/uv/ebm/StructureDefinition/adaptation"] },
            "extension": [
                {
                    "url": "http://hl7.org/fhir/StructureDefinition/artifact-status",
                    "valueCode": "active"
                },
                {
                    "url": "http://hl7.org/fhir/StructureDefinition/artifact-author",
                    "valueContactDetail": { "name": userName }
                }
            ],
            "title": userName + "'s Change Report for " + (resourceLoaded.json.title || resourceLoaded.json.name),
            "artifactReference": {
                "reference": "Composition/" + resourceLoaded.json.id,
                "type": "Composition",
                "display": resourceLoaded.json.title || resourceLoaded.json.name
            },
            "date": new Date().toISOString(),
            "content": []
        };
        for (const change of resourceChanges) {
            let originalValue = "";
            if (change.originalValue !== null && change.originalValue !== undefined) {
                if (typeof change.originalValue === "object") {
                    originalValue = JSON.stringify(change.originalValue);
                    if (change.originalDatatype === "CodeableConcept") {
                        originalValue = displayStringFromFHIRCodeableConcept(change.originalValue);
                    } else if (change.originalDatatype === "Period") {
                        originalValue = displayStringFromFHIRPeriod(change.originalValue);
                    } else if (change.originalDatatype === "Coding") {
                        originalValue = displayStringFromFHIRCoding(change.originalValue);
                    } else if (change.originalDatatype === "Expression") {
                        originalValue = displayStringFromFHIRExpression(change.originalValue);
                    } else if (change.originalDatatype === "Quantity") {
                        originalValue = displayStringFromFHIRQuantity(change.originalValue);
                    } else if (change.originalDatatype === "Range") {
                        originalValue = displayStringFromFHIRRange(change.originalValue);
                    } else if (change.originalDatatype === "Reference") {
                        originalValue = displayStringFromFHIRReference(change.originalValue);
                    } else if (change.originalDatatype === "RelativeTime") {
                        originalValue = displayStringFromFHIRRelativeTime(change.originalValue);
                    }
                } else if (typeof change.originalValue === "string") {
                    originalValue = change.originalValue;
                } else {
                    originalValue = change.originalValue.toString();
                }
            }
            let newValue = "";
            if (change.newValue !== null && change.newValue !== undefined) {
                if (typeof change.newValue === "object") {
                    newValue = JSON.stringify(change.newValue)
                    if (change.newDatatype === "CodeableConcept") {
                        newValue = displayStringFromFHIRCodeableConcept(change.newValue);
                    } else if (change.newDatatype === "Period") {
                        newValue = displayStringFromFHIRPeriod(change.newValue);
                    } else if (change.newDatatype === "Coding") {
                        newValue = displayStringFromFHIRCoding(change.newValue);
                    } else if (change.newDatatype === "Expression") {
                        newValue = displayStringFromFHIRExpression(change.newValue);
                    } else if (change.newDatatype === "Quantity") {
                        newValue = displayStringFromFHIRQuantity(change.newValue);
                    } else if (change.newDatatype === "Range") {
                        newValue = displayStringFromFHIRRange(change.newValue);
                    } else if (change.newDatatype === "Reference") {
                        newValue = displayStringFromFHIRReference(change.newValue);
                    } else if (change.newDatatype === "RelativeTime") {
                        newValue = displayStringFromFHIRRelativeTime(change.newValue);
                    }
                } else if (typeof change.newValue === "string") {
                    newValue = change.newValue;
                } else {
                    newValue = change.newValue.toString();
                }
            }
            let contentEntry = {
                "type": { "text": "adapted element" },
                "classifier": [{ "text": change.originalDatatype }],
                "path": ["Resource FOI " + change.resourceId, change.originalPath],
                "component": [
                    {
                        "summary": originalValue,
                        "type": {
                            "coding": [
                                {
                                    "system": "https://fevir.net/resources/CodeSystem/179423",
                                    "code": "OriginalResourceContent",
                                    "display": "Original Resource Content"
                                }
                            ]
                        }
                    },
                    {
                        "summary": newValue,
                        "type": {
                            "coding": [
                                {
                                    "system": "https://fevir.net/resources/CodeSystem/179423",
                                    "code": "SuggestedChange",
                                    "display": "Suggested Change to Resource Content"
                                }
                            ]
                        }
                    }
                ]
            };
            changeReportJson.content.push(contentEntry);
        }
        let response = await submitFhirJson(changeReportJson, changeReportJson.title, apiToken);
        let resourceFOI;
        if (response?.success) {
            resourceFOI = response.formstateid;
            alert("Change Report created -- view at https://fevir.net/" + resourceFOI);
        }
    }

    const generateSummaryOfFindings = async () => {
        let sofJson = generateSummaryOfFindingsFromOutcomeMeasureReport(resourceLoaded.json, resourceLoaded.resourceDictionary);
        let resourceFOI = await submitNewCompositionAndBundle(sofJson, apiToken) || "";
        if (resourceFOI) {
            let newResourceDetail = JSON.parse(JSON.stringify(parentResourceDetail));
            let relatesToSoF = {
                "type": "transformed-into",
                "targetReference": {
                    "reference": "Composition/" + resourceFOI,
                    "type": "Composition",
                    "display": sofJson.title
                }
            }
            let oldRelatesTo = newResourceDetail.resourceJson.relatesTo;
            let midRelatesTo = newResourceDetail.revisedResourceJson.relatesTo;
            let newRelatesTo = [];
            if (midRelatesTo) {
                newRelatesTo = midRelatesTo.filter((item) => { return item.type !== "transformed-into" });
            } else if (oldRelatesTo) {
                newRelatesTo = oldRelatesTo.filter((item) => { return item.type !== "transformed-into" });
            }
            newRelatesTo.push(relatesToSoF);
            newResourceDetail.revisedResourceJson.relatesTo = newRelatesTo;
            console.log("resourceUpdate for Composition.relatesTo");
            dispatch(resourceUpdated(newResourceDetail));
            updateChangeReport(dispatch, resourceChanges, newResourceDetail.id,
                "ArrayOfRelatesTo",
                "relatesTo",
                oldRelatesTo,
                newRelatesTo);
        }
        setSofFoiState(resourceFOI);
    }

    if (!resourceLoaded.title) {
        return <Spinner />;
        //return <p><b>ERROR: Resource not properly loaded</b></p>
    }

    return <div style={{ display: "flex" }}>
        <div className="leftnav unselectable" style={{ float: "left" }}>
            <TopSectionNavigation sections={resourceLoaded.sections}
                outcomeMeasureReport={(resourceLoaded?.json?.type?.text === "Outcome Measure Report")}
                hasResourceCollection={resourceLoaded.hasResourceCollection}
            />
            <CompositionPdfViewer fhirJson={resourceLoaded.json} />
            {editPermission && <div style={{marginTop: "10px"}}>
                {editMode ?
                    <button type="button"
                        onClick={() => { dispatch(editModeOff()); }} >VIEW</button>
                    :
                    <button type="button"
                        onClick={() => { dispatch(editModeOn()); }} >EDIT</button>
                }
                {unsavedChanges === "hasChanges" && <>
                    <br />
                    <button type="button" style={{ marginLeft: "12px" }}
                        onClick={() => { saveAllChanges(); }} >Save All Changes</button>
                </>}
                {resourceChanges.length > 0 && <>
                    <br />
                    <button type="button" style={{ marginLeft: "12px" }}
                        onClick={() => { createChangeReport(); }} >Create Change Report</button>
                </>}
                {(resourceLoaded?.json?.type?.text === "Outcome Measure Report") && <>
                    <br />
                    {sofFoiState ? <>
                        <span>
                            <a href={"https://computablepublishing.com/Composition/" + sofFoiState}
                                target='_blank' rel='noopener noreferrer'>Go To Summary of Findings</a>
                            <span>
                                <br />
                            </span><a href={"https://fevir.net/resources/Composition/" + sofFoiState}
                                target='_blank' rel='noopener noreferrer'>or on FEvIR Platform</a>
                            <br />
                            <button type="button" style={{ marginLeft: "12px" }}
                                onClick={() => { generateSummaryOfFindings(); }}
                            >Replace (Create a New) Summary of Findings</button>
                        </span>
                    </>
                        :
                        <button type="button" style={{ marginLeft: "12px" }}
                            onClick={() => { generateSummaryOfFindings(); }}
                        >Create Summary of Findings</button>
                    }
                </>}
            </div>}
        </div>
        <div className="rightmain"
            style={{ float: "right" }}>
            <h1>{resourceLoaded.title}</h1>
            <CompositionPdfViewer fhirJson={resourceLoaded.json} />
            <Segment id="CompositionSegment">
                <div id="top">
                    {editMode ?
                        <NarrativeEntry elementName="text"
                            startingValue={compositionState.text}
                            generatedNarrative={resourceLoaded.generatedNarrative}
                            setResourceState={setCompositionState} />
                        :
                        <>{(resourceLoaded?.json?.text?.div &&
                            resourceLoaded.json.text.div !== "<div xmlns=\"http://www.w3.org/1999/xhtml\">[No data.]</div>" &&
                            resourceLoaded.json.text.status !== "empty") &&
                            <ReactMarkdown children={resourceLoaded.json.text.div} rehypePlugins={[rehypeRaw]} />}</>
                    }
                </div>
                {((resourceLoaded?.json?.type?.text === "Outcome Measure Report" ||
                    resourceLoaded?.json?.type?.text === "Baseline Measure Report" ||
                    resourceLoaded?.json?.type?.text === "Participant Flow Report") &&
                    compositionState.section?.length > 0) &&
                    <div id="evidenceTable">
                        {editMode ?
                            <EvidenceTableEdit parentResourceDetail={parentResourceDetail}
                                resourceLoaded={resourceLoaded}
                            />
                            :
                            <EvidenceTableDisplay resourceLoaded={resourceLoaded} />}
                    </div>}
                {editMode && <div>
                    <Button className="formButton" style={{ margin: "0px" }}
                        content="Add/Delete/Reorganize Sections" compact
                        onClick={() => { setEditSectionModal({ "openModal": true }) }} />
                    <br />
                </div>}
                {editSectionModal.openModal && <EditSectionArrayModal editSectionModal={editSectionModal}
                    setEditSectionModal={setEditSectionModal}
                    compositionState={compositionState}
                    setCompositionState={setCompositionState} />}
                {(compositionState.section?.length > 0) &&
                    compositionState.section.map((section, sectionIndex) => {
                        return <div key={sectionIndex}>
                            <TopSectionDisplay foi={foi} section={section}
                                sectionPath={[sectionIndex]} sectionIndex={sectionIndex}
                                parentResourceDetail={parentResourceDetail}
                                resourceLoaded={resourceLoaded} />
                            <br />
                        </div>
                    })}
                <MetadataDisplay fhirJson={resourceLoaded.json}
                    parentResourceDetail={parentResourceDetail} />
                {resourceLoaded.hasResourceCollection && <>
                    <h2 id="resource-collection">Resource Collection</h2>
                    <div style={{ textAlign: "left" }}>
                        {allResources.map((entry, index) => {
                            if (entry.resourceType === "Composition") {
                                const cpUrl = "https://computablepublishing.com/Composition/" + entry.id;
                                const fevirUrl = "https://fevir.net/resources/Composition/" + entry.id;
                                return <div key={index}>
                                    <a href={cpUrl} target='_blank' rel='noopener noreferrer'>
                                        <b>Composition: </b>{entry.displayTitle}
                                    </a> or on <a href={fevirUrl}
                                        target='_blank' rel='noopener noreferrer'>FEvIR Platform (FOI {entry.id})</a>
                                </div>
                            } else {
                                const url = "https://fevir.net/resources/" + entry.resourceType + "/" + entry.id;
                                return <div key={index}>
                                    <a href={url} target='_blank' rel='noopener noreferrer'>
                                        <b>{entry.resourceType}: </b>{entry.displayTitle} (on FEvIR Platform FOI {entry.id})
                                    </a>
                                </div>
                            }
                        })}
                    </div>
                </>}
            </Segment>
        </div>
    </div>
}

export default CompositionDisplay;
export { createEvidenceDisplay };